import React, { useEffect, useMemo, useState, useCallback } from 'react';
import OverflowSafeMenu, { MenuItem } from './common/OverflowSafeMenu';
import { useDispatch, useSelector } from 'react-redux';
import {
    FileText, Search, Filter, Download, Send, DollarSign, Clock,
    CheckCircle, AlertTriangle, MoreVertical, Eye, X, ChevronLeft,
    ChevronRight, Phone, Car, RefreshCw, Ban, CreditCard, Plus,
    Trash2, Edit2, Save, Loader, MessageSquare, Calendar,
    ExternalLink, User, Archive, TrendingUp, Target, Percent,
    LineChart, Table as TableIcon, Wrench,
} from 'lucide-react';
import Layout from './Layout';
import IncomeChart from './income/IncomeChart';
import RecordCrossLink from './RecordCrossLink';
import LaneLoading from './LaneLoading';
import { useOpenJob, useConsumeRecordParam, INVOICE_PARAM } from '../hooks/useRecordNavigation';
import { CHART_MODES } from '../utils/incomeChartSeries';
import {
    fetchInvoices,
    fetchInvoice,
    fetchInvoicesByStatus,
    fetchOutstandingInvoices,
    searchInvoices,
    advancedSearchInvoices,
    sendInvoice,
    voidInvoice,
    reconcileInvoice,
    reconcileInvoicesBatch,
    updateInvoice,
    recordPayment,
    addLineItem,
    updateLineItem,
    deleteLineItem,
    fetchLineItemTypes,
    fetchPaymentMethods,
    openInvoicePdf,
    downloadInvoicePdf,
    setStatusFilter,
    setSearchQuery,
    setServerFilter,
    clearServerFilter,
    setPage,
    closeDetailPanel,
    openSendConfirm,
    closeSendConfirm,
    openVoidConfirm,
    closeVoidConfirm,
    openPaymentModal,
    closePaymentModal,
    clearOperationStatus,
    fetchInvoiceArchive,
    searchInvoiceArchive,
    setArchivePage,
    fetchAnnualIncome,
    fetchMonthlyIncome,
    fetchWeeklyIncome,
    fetchIncomeYears,
    saveSalesTarget,
    removeSalesTarget,
} from '../store/slices/invoiceSlice';
import { listAccountingIntegrationsApi } from '../services/accountingIntegrationApi';
import { getMyStaffApi } from '../services/staffApi';
import { getCustomerVehiclesApi, addCustomerVehicleApi } from '../services/customerVehicleApi';
import { createJobForInvoiceApi } from '../services/invoiceApi';
import { updateCustomerApi } from '../services/customerApi';
import { formatAudOrZero } from '../utils/money';

// ============================================================
// Constants
// ============================================================

// Fallback options used only if the backend GET /invoices/line-item-types
// hasn't responded yet. The real source of truth is the QuoteLineItem.LineType
// enum on the server, fetched on mount via fetchLineItemTypes.
const LINE_TYPE_OPTIONS_FALLBACK = [
    { value: 'LABOUR',      label: 'Labour' },
    { value: 'PARTS',       label: 'Parts' },
    { value: 'CONSUMABLES', label: 'Consumables' },
    { value: 'LOAN_CAR',    label: 'Loan Car' },
    { value: 'ADJUSTMENT',  label: 'Adjustment' },
    { value: 'DISCOUNT',    label: 'Discount' },
    { value: 'OTHER',       label: 'Other' },
];

const PAYMENT_METHOD_FALLBACK = [
    { value: 'EFTPOS', label: 'EFTPOS' },
];

const TERMINAL_STATUSES = ['PAID', 'VOID', 'RECONCILED'];

/*
 * Most invoices one batch can reconcile. Mirrors MAX_RECONCILE_BATCH in InvoiceService —
 * the server rejects anything larger, and a selection that can only fail is worse than one
 * that stops accepting ticks.
 *
 * Small because reconciling is now several calls to Xero or MYOB per invoice, all inside
 * the one request the user is waiting on.
 */
const MAX_RECONCILE_BATCH = 10;
const EDITABLE_STATUSES = ['DRAFT', 'SENT'];
const LINE_ITEM_EDITABLE_STATUSES = ['DRAFT'];

// Status tab definitions — each tab shows one or more statuses.
// The 'PARTIALLY_PAID' tab widens to also include TRADE_CREDIT invoices
// via the /invoices/outstanding endpoint; both render with their own
// status badge so staff can distinguish them.
const STATUS_TABS = [
    { key: 'all',           label: 'All' },
    { key: 'DRAFT',         label: 'Draft' },
    { key: 'SENT',          label: 'Sent' },
    { key: 'PARTIALLY_PAID',label: 'Partial' },
    { key: 'PAID',          label: 'Paid' },
    { key: 'OVERDUE',       label: 'Overdue' },
    { key: 'VOID',          label: 'Void' },
];

// ============================================================
// Helpers
// ============================================================

// Workshop-side: a missing amount reads as $0.00 here, which is the long-standing
// behaviour of this page. See utils/money.js for why that is now an explicit choice.
const formatCurrency = formatAudOrZero;

const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatDateTime = (ts) => {
    if (!ts) return '—';
    return new Date(ts).toLocaleString('en-AU', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
};

const getLineTypeLabel = (value, options = LINE_TYPE_OPTIONS_FALLBACK) =>
    options.find(o => o.value === value)?.label ?? value ?? '—';

// ============================================================
// Status Badge
// ============================================================

const StatusBadge = ({ status, size = 'sm' }) => {
    const styles = {
        PAID:           'bg-emerald-100 text-emerald-700',
        SENT:           'bg-blue-100 text-blue-700',
        OVERDUE:        'bg-red-100 text-red-700',
        DRAFT:          'bg-gray-100 text-gray-600',
        PARTIALLY_PAID: 'bg-amber-100 text-amber-700',
        TRADE_CREDIT:   'bg-orange-100 text-orange-700',
        VOID:           'bg-slate-100 text-slate-500',
        RECONCILED:     'bg-teal-100 text-teal-700',
    };
    const icons = {
        PAID:           <CheckCircle size={12} />,
        SENT:           <Send size={12} />,
        OVERDUE:        <AlertTriangle size={12} />,
        DRAFT:          <Clock size={12} />,
        PARTIALLY_PAID: <DollarSign size={12} />,
        TRADE_CREDIT:   <DollarSign size={12} />,
        VOID:           <Ban size={12} />,
        RECONCILED:     <Archive size={12} />,
    };
    const labels = {
        PAID: 'Paid', SENT: 'Sent', OVERDUE: 'Overdue',
        DRAFT: 'Draft', PARTIALLY_PAID: 'Partial',
        TRADE_CREDIT: 'Trade Credit', VOID: 'Void',
        RECONCILED: 'Reconciled',
    };
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-600'}`}>
            {icons[status]} {labels[status] || status}
        </span>
    );
};

// ============================================================
// PdfButton — shared button with per-invoice spinner
// ============================================================

const PdfButton = ({ invoice, pdfLoadingUuid, dispatch, variant = 'icon', label = 'View PDF' }) => {
    const isLoading = pdfLoadingUuid === invoice.uuid;
    const handleOpen = (e) => {
        e.stopPropagation();
        dispatch(openInvoicePdf({ uuid: invoice.uuid, invoiceNumber: invoice.invoiceNumber }));
    };
    const handleDownload = (e) => {
        e.stopPropagation();
        dispatch(downloadInvoicePdf({ uuid: invoice.uuid, invoiceNumber: invoice.invoiceNumber }));
    };

    if (variant === 'icon') {
        return (
            <button
                onClick={handleOpen}
                disabled={isLoading}
                title="View PDF"
                className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors disabled:opacity-50"
            >
                {isLoading ? <Loader size={16} className="animate-spin" /> : <ExternalLink size={16} />}
            </button>
        );
    }

    // Full button variant for the detail panel action bar
    return (
        <div className="flex items-center gap-1 ml-auto">
            <button
                onClick={handleOpen}
                disabled={isLoading}
                className="flex items-center gap-1.5 px-3 py-1.5 text-gray-600 text-sm font-medium rounded-lg hover:bg-gray-100 border border-gray-200 transition-colors disabled:opacity-50"
            >
                {isLoading ? <Loader size={14} className="animate-spin" /> : <ExternalLink size={14} />}
                View PDF
            </button>
            <button
                onClick={handleDownload}
                disabled={isLoading}
                title="Download PDF"
                className="flex items-center gap-1.5 px-3 py-1.5 text-gray-600 text-sm font-medium rounded-lg hover:bg-gray-100 border border-gray-200 transition-colors disabled:opacity-50"
            >
                <Download size={14} />
            </button>
        </div>
    );
};

// ============================================================
// InvoiceDetailPanel
// ============================================================

const InvoiceDetailPanel = ({ invoice, detailLoading, operationLoading, operationError, pdfLoadingUuid, lineTypeOptions = LINE_TYPE_OPTIONS_FALLBACK, paymentMethodOptions = PAYMENT_METHOD_FALLBACK, hasAccountingIntegration = false, onOpenJob = () => {}, openingJobUuid = null, dispatch }) => {
    const isTerminal = TERMINAL_STATUSES.includes(invoice?.status);
    const isEditable = EDITABLE_STATUSES.includes(invoice?.status);
    const canEditLineItems = LINE_ITEM_EDITABLE_STATUSES.includes(invoice?.status);

    const [editingHeader, setEditingHeader] = useState(false);
    const [headerForm, setHeaderForm] = useState({});
    const [editingLineItem, setEditingLineItem] = useState(null);
    const [lineItemForm, setLineItemForm] = useState({});
    const [savingLineItem, setSavingLineItem] = useState(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const [addForm, setAddForm] = useState({
        lineType: 'ADJUSTMENT', description: '', quantity: '1', unitPrice: '', comment: '',
    });
    const [paymentForm, setPaymentForm] = useState({ amount: '', paymentMethod: 'EFTPOS', notes: '' });
    const [voidReason, setVoidReason] = useState('');
    const [showVoidLocal, setShowVoidLocal] = useState(false);
    const [showSendLocal, setShowSendLocal] = useState(false);
    const [showPaymentLocal, setShowPaymentLocal] = useState(false);
    const [showReconcileLocal, setShowReconcileLocal] = useState(false);

    // Delete confirmation state
    const [deleteTarget, setDeleteTarget] = useState(null); // { uuid, description }

    // The signed-in person's name, fetched only when they claim an invoice.
    const [myName, setMyName] = useState('');

    // ── Raising a job for an invoice that has none ──────────────────────────────
    // Every migrated invoice is in that state, so this is the common case, not an edge.
    // Contact details are routinely missing after a migration — the Customers export
    // carries an email, the Jobs and Credit Notes exports often do not.
    const [editingContact, setEditingContact] = useState(false);
    const [contactForm, setContactForm] = useState({ email: '', phone: '' });
    const [contactBusy, setContactBusy] = useState(false);
    const [contactError, setContactError] = useState(null);

    const [showCreateJob, setShowCreateJob] = useState(false);
    const [vehicles, setVehicles] = useState(null);      // null = not loaded yet
    const [chosenVehicle, setChosenVehicle] = useState('');
    const [newRego, setNewRego] = useState('');
    const [jobBusy, setJobBusy] = useState(false);
    const [jobError, setJobError] = useState(null);

    /**
     * Escape closes the panel.
     *
     * 🔑 The panel covers the list and its only exit was a small × in the corner. Escape
     * is what people already press at a thing that has slid over what they were reading,
     * and it costs nothing to honour.
     *
     * <p>⚠️ Only when nothing inside is open. This panel stacks its own dialogs —
     * payment, void, reconcile, the delete confirmation — and closing the whole panel
     * out from under one of those loses whatever was half-entered in it. Escape reaches
     * the innermost thing, which is what a person means by it.
     */
    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key !== 'Escape') return;
            if (showPaymentLocal || showVoidLocal || showReconcileLocal || deleteTarget) return;
            // A field being edited: Escape belongs to the edit, not the panel.
            if (editingHeader || editingLineItem || showAddForm) return;
            dispatch(closeDetailPanel());
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [dispatch, showPaymentLocal, showVoidLocal, showReconcileLocal, deleteTarget,
        editingHeader, editingLineItem, showAddForm]);

    useEffect(() => {
        if (invoice) {
            setHeaderForm({
                issueDate: invoice.issueDate || '',
                dueDate: invoice.dueDate || '',
                estimatedValue: invoice.estimatedValue ?? '',
                notes: invoice.notes || '',
                claimCreatedBy: false,
            });
            setEditingContact(false);
            setContactError(null);
            setShowCreateJob(false);
            setVehicles(null);
            setChosenVehicle('');
            setNewRego('');
            setJobError(null);
            setEditingHeader(false);
            setEditingLineItem(null);
            setSavingLineItem(null);
            setShowAddForm(false);
            setDeleteTarget(null);
        }
    }, [invoice?.uuid]);

    if (!invoice) return null;

    /**
     * Take this invoice, and show WHOSE name it will carry.
     *
     * 🔑 The name is fetched here and nowhere else. The token holds an email and no
     * name, so the browser cannot otherwise say who "me" is — and asking on every page
     * load, to fill a label almost nobody opens, is a request per invoice view for
     * nothing. This is the one moment the answer matters.
     *
     * ⚠️ A failed lookup does NOT block the claim. The server resolves the staff record
     * itself from the token; this call only supplies a label, so losing it costs the
     * confirmation and not the action.
     */
    const handleClaimCreatedBy = async () => {
        setHeaderForm(f => ({ ...f, claimCreatedBy: true }));
        if (myName) return;
        try {
            const me = await getMyStaffApi();
            // fullName is the server's own rendering of the same two columns the invoice
            // shows, so the two agree by construction rather than by both being joined
            // the same way here and hoping.
            const name = (me?.fullName
                || [me?.firstName, me?.lastName].filter(Boolean).join(' ')).trim();
            if (name) setMyName(name);
        } catch {
            // Left as "You" — see above.
        }
    };

    /**
     * Whether this invoice already says what the button would make it say.
     *
     * 🔑 Without this the button looks broken on the commonest case there is. Whoever
     * ran the import IS the person now reading the invoice, so "Set to me" replaced a
     * name with the identical name and read as a change that jumped straight back. It
     * had not: it had never been anything else.
     */
    const alreadyMine = Boolean(myName) && myName === invoice.createdByName;

    /**
     * Open the job panel and load the customer's cars.
     *
     * 🔑 Loaded on OPEN, not on every invoice view. Most invoices already have a job and
     * would never use the answer, and this runs on a list people page through.
     */
    const openCreateJob = async () => {
        setShowCreateJob(true);
        setJobError(null);
        if (vehicles !== null) return;
        try {
            const list = await getCustomerVehiclesApi(invoice.customerUuid);
            setVehicles(list);
            // One car is not a choice — preselect it and let them confirm.
            if (list.length === 1) setChosenVehicle(list[0].uuid);
        } catch {
            setVehicles([]);
            setJobError('Could not load this customer\'s vehicles. You can still add one by rego.');
        }
    };

    /**
     * Create the job, adding the vehicle first when a rego was typed.
     *
     * ⚠️ Two writes, and the vehicle one is NOT undone if the job then fails. That is
     * deliberate: the car is real either way and the workshop has just told us about it,
     * so keeping it means the retry is one click rather than re-typing. The rego field
     * is cleared on success only.
     */
    const submitCreateJob = async () => {
        setJobBusy(true);
        setJobError(null);
        try {
            let vehicleUuid = chosenVehicle;
            if (!vehicleUuid && newRego.trim()) {
                const added = await addCustomerVehicleApi(invoice.customerUuid,
                    { regoNumber: newRego.trim() });
                vehicleUuid = added.uuid;
                setVehicles((v) => [...(v ?? []), added]);
                setChosenVehicle(added.uuid);
                setNewRego('');
            }
            if (!vehicleUuid) {
                setJobError('Choose a vehicle, or add one by registration number.');
                return;
            }
            await createJobForInvoiceApi(invoice.uuid, vehicleUuid);
            setShowCreateJob(false);
            dispatch(fetchInvoice(invoice.uuid));
        } catch (err) {
            setJobError(err?.response?.data?.message || 'Could not create the job.');
        } finally {
            setJobBusy(false);
        }
    };

    /**
     * Fix the customer's contact details without leaving the invoice.
     *
     * 🔑 This is where the gap is noticed. An invoice with no email is the moment
     * somebody tries to SEND it and cannot, and making them navigate to the customer
     * record and back loses their place in a list of twelve thousand.
     *
     * <p>⚠️ It edits the CUSTOMER, not this invoice — the same record every other
     * invoice of theirs reads. That is what makes it worth doing once, and why the
     * confirmation says so.
     */
    const saveContact = async () => {
        setContactBusy(true);
        setContactError(null);
        try {
            await updateCustomerApi(invoice.customerUuid, {
                // Empty means "leave it alone" on this endpoint, so a blank field does
                // not wipe a detail somebody else filled in.
                email: contactForm.email.trim() || null,
                phone: contactForm.phone.trim() || null,
            });
            setEditingContact(false);
            dispatch(fetchInvoice(invoice.uuid));
        } catch (err) {
            setContactError(err?.response?.data?.message
                || 'Could not save. That email may already belong to another customer.');
        } finally {
            setContactBusy(false);
        }
    };

    const handleHeaderSave = () => {
        dispatch(updateInvoice({
            uuid: invoice.uuid,
            data: {
                issueDate: headerForm.issueDate || null,
                dueDate: headerForm.dueDate || null,
                estimatedValue: headerForm.estimatedValue !== '' ? parseFloat(headerForm.estimatedValue) : null,
                notes: headerForm.notes || null,
                // Sent only when asked for. The server ignores false and null alike.
                claimCreatedBy: headerForm.claimCreatedBy || undefined,
            },
        }));
        setEditingHeader(false);
    };

    const handleStartEditLineItem = (li) => {
        setEditingLineItem(li.uuid);
        setLineItemForm({ description: li.description, quantity: String(li.quantity), unitPrice: String(li.unitPrice) });
    };

    const handleCancelEditLineItem = () => {
        if (savingLineItem) return;
        setEditingLineItem(null);
        setLineItemForm({});
    };

    const handleSaveLineItem = async (li) => {
        const trimmedDescription = lineItemForm.description?.trim();
        if (!trimmedDescription || !lineItemForm.unitPrice || !lineItemForm.quantity) return;
        setSavingLineItem(li.uuid);
        try {
            await dispatch(updateLineItem({
                invoiceUuid: invoice.uuid,
                lineItemUuid: li.uuid,
                data: {
                    description: trimmedDescription,
                    quantity: parseFloat(lineItemForm.quantity),
                    unitPrice: parseFloat(lineItemForm.unitPrice),
                },
            })).unwrap();
            setEditingLineItem(null);
            setLineItemForm({});
        } catch (_) {
            // error shown in panel banner
        } finally {
            setSavingLineItem(null);
        }
    };

    const handleLineItemKeyDown = (e, li) => {
        if (e.key === 'Enter') { e.preventDefault(); handleSaveLineItem(li); }
        else if (e.key === 'Escape') { handleCancelEditLineItem(); }
    };

    // Opens the in-panel confirmation dialog instead of a browser confirm()
    const handleDeleteLineItem = (li) => {
        setDeleteTarget({ uuid: li.uuid, description: li.description });
    };

    const handleConfirmDelete = async () => {
        if (!deleteTarget) return;
        try {
            await dispatch(deleteLineItem({
                invoiceUuid: invoice.uuid,
                lineItemUuid: deleteTarget.uuid,
            })).unwrap();
        } catch (_) {
            // error shown in panel banner
        } finally {
            setDeleteTarget(null);
        }
    };

    const handleAddLineItem = () => {
        if (!addForm.description.trim() || !addForm.unitPrice) return;
        dispatch(addLineItem({
            invoiceUuid: invoice.uuid,
            data: {
                lineType: addForm.lineType,
                description: addForm.description.trim(),
                quantity: parseFloat(addForm.quantity) || 1,
                unitPrice: parseFloat(addForm.unitPrice),
                comment: addForm.comment.trim() || null,
            },
        }));
        setAddForm({ lineType: 'ADJUSTMENT', description: '', quantity: '1', unitPrice: '', comment: '' });
        setShowAddForm(false);
    };

    const handleRecordPayment = () => {
        const isTradeCredit = paymentForm.paymentMethod === 'TRADE_CREDIT';
        // Trade credit submits $0 as a marker; everything else needs a real amount.
        if (!isTradeCredit && !paymentForm.amount) return;
        const amount = isTradeCredit ? 0 : parseFloat(paymentForm.amount);
        dispatch(recordPayment({
            uuid: invoice.uuid,
            data: {
                amount,
                paymentMethod: paymentForm.paymentMethod,
                notes: paymentForm.notes || null,
            },
        }));
        setShowPaymentLocal(false);
        setPaymentForm({ amount: '', paymentMethod: 'EFTPOS', notes: '' });
    };

    const handleSendInvoice = () => {
        dispatch(sendInvoice({ uuid: invoice.uuid }));
        setShowSendLocal(false);
    };

    const handleVoidInvoice = () => {
        dispatch(voidInvoice({ uuid: invoice.uuid, reason: voidReason }));
        setVoidReason('');
        setShowVoidLocal(false);
    };

    const handleReconcileInvoice = () => {
        dispatch(reconcileInvoice({ uuid: invoice.uuid }));
        setShowReconcileLocal(false);
    };

    const lineItems = invoice.lineItems || [];
    const addFormTotal = (parseFloat(addForm.quantity) || 0) * (parseFloat(addForm.unitPrice) || 0);

    return (
        <div className="flex flex-col h-full">
            {/* Panel Header */}
            <div className="flex items-start justify-between p-6 border-b border-gray-100 shrink-0">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center shrink-0">
                        <FileText size={20} className="text-indigo-600" />
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <span className="font-mono text-lg font-bold text-gray-900">{invoice.invoiceNumber}</span>
                            <StatusBadge status={invoice.status} />
                        </div>
                        <p className="text-sm text-gray-500 mt-0.5">
                            {invoice.bookingCode && (
                                <span className="font-mono mr-2 text-indigo-500">{invoice.bookingCode}</span>
                            )}
                            {/* The job behind this invoice. jobUuid is the server's
                                answer; a migrated invoice has none, and then the number
                                stays as text rather than becoming a dead link. */}
                            {invoice.jobNumber && (
                                <RecordCrossLink
                                    variant="inline"
                                    className="mr-2"
                                    uuid={invoice.jobUuid}
                                    label={invoice.jobNumber}
                                    icon={Wrench}
                                    onOpen={onOpenJob}
                                    loading={openingJobUuid === invoice.jobUuid}
                                    title={`Open job ${invoice.jobNumber}`}
                                />
                            )}
                            Created {formatDate(invoice.issueDate)}
                        </p>
                    </div>
                </div>
                <button onClick={() => dispatch(closeDetailPanel())} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 transition-colors">
                    <X size={20} />
                </button>
            </div>

            {/* The same card-shaped wait the job lanes use, rather than a third
                pattern: the panel is about to hold cards, so it shows their shapes. */}
            {detailLoading && <LaneLoading className="p-6" cards={2} label="Loading invoice details" />}

            {!detailLoading && (
                <div className="flex-1 overflow-y-auto">
                    {operationError && (
                        <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-sm text-red-700">
                            <AlertTriangle size={16} />{operationError}
                        </div>
                    )}

                    {/* Action Bar */}
                    <div className="flex items-center gap-2 px-6 pt-5 pb-4 border-b border-gray-50 flex-wrap shrink-0">
                        {/* 🔑 Not DRAFT only. "I never got that invoice" had no answer
                            short of voiding it and raising a new one. A VOID invoice is
                            the one case where sending is actively wrong — there is
                            nothing to collect and the document contradicts itself.
                            Re-sending never changes the status; see sendInvoice. */}
                        {invoice.status !== 'VOID' && (
                            <button onClick={() => setShowSendLocal(true)} disabled={operationLoading}
                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50">
                                <Send size={14} /> {invoice.status === 'DRAFT' ? 'Send Invoice' : 'Resend Invoice'}
                            </button>
                        )}
                        {['SENT', 'PARTIALLY_PAID', 'TRADE_CREDIT', 'OVERDUE'].includes(invoice.status) && (
                            <button onClick={() => setShowPaymentLocal(true)} disabled={operationLoading}
                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50">
                                <CreditCard size={14} /> Record Payment
                            </button>
                        )}
                        {/* Reconcile means "settled in the accounting system". With none
                            connected the action has no meaning, so it is not offered. */}
                        {invoice.status === 'PAID' && hasAccountingIntegration && (
                            <button onClick={() => setShowReconcileLocal(true)} disabled={operationLoading}
                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50">
                                <Archive size={14} /> Reconcile
                            </button>
                        )}
                        {!isTerminal && (
                            <button onClick={() => setShowVoidLocal(true)} disabled={operationLoading}
                                    className="flex items-center gap-1.5 px-3 py-1.5 text-red-600 text-sm font-medium rounded-lg hover:bg-red-50 border border-red-200 transition-colors disabled:opacity-50">
                                <Ban size={14} /> Void
                            </button>
                        )}

                        {/* PDF buttons — always visible */}
                        <PdfButton invoice={invoice} pdfLoadingUuid={pdfLoadingUuid} dispatch={dispatch} variant="full" />
                    </div>

                    <div className="px-6 py-5 space-y-6">
                        {/* Customer & Vehicle */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="bg-gray-50 rounded-xl p-4">
                                <div className="flex items-start justify-between gap-2">
                                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Customer</p>
                                    {!editingContact && invoice.customerUuid && (
                                        <button type="button"
                                            onClick={() => {
                                                setContactForm({
                                                    email: invoice.customerEmail || '',
                                                    phone: invoice.customerPhone || '',
                                                });
                                                setContactError(null);
                                                setEditingContact(true);
                                            }}
                                            className="text-xs text-indigo-600 hover:text-indigo-700 font-medium">
                                            Edit
                                        </button>
                                    )}
                                </div>
                                <p className="font-semibold text-gray-900">{invoice.customerName}</p>

                                {!editingContact ? (
                                    <>
                                        {invoice.customerEmail
                                            ? <p className="text-sm text-gray-500 mt-0.5">{invoice.customerEmail}</p>
                                            : <p className="text-sm text-amber-700 mt-0.5">No email — this invoice cannot be sent</p>}
                                        {invoice.customerPhone && (
                                            <p className="text-sm text-gray-500 flex items-center gap-1 mt-0.5">
                                                <Phone size={12} /> {invoice.customerPhone}
                                            </p>
                                        )}
                                    </>
                                ) : (
                                    <div className="space-y-2 mt-2">
                                        <input type="email" value={contactForm.email}
                                            onChange={(e) => setContactForm(f => ({ ...f, email: e.target.value }))}
                                            placeholder="Email address"
                                            className="w-full px-2 py-1 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none" />
                                        <input type="tel" value={contactForm.phone}
                                            onChange={(e) => setContactForm(f => ({ ...f, phone: e.target.value }))}
                                            placeholder="Phone"
                                            className="w-full px-2 py-1 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none" />
                                        {contactError && (
                                            <p className="text-[11px] text-red-700">{contactError}</p>
                                        )}
                                        {/* Said plainly: this is not a per-invoice detail. */}
                                        <p className="text-[11px] text-gray-500">
                                            Saves on the customer, so every invoice of theirs shows it.
                                        </p>
                                        <div className="flex items-center gap-2">
                                            <button type="button" onClick={saveContact} disabled={contactBusy}
                                                className="text-xs font-semibold bg-indigo-600 text-white px-2.5 py-1 rounded-lg hover:bg-indigo-700 disabled:opacity-50">
                                                {contactBusy ? 'Saving…' : 'Save'}
                                            </button>
                                            <button type="button" onClick={() => setEditingContact(false)}
                                                className="text-xs text-gray-500 hover:text-gray-700">Cancel</button>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="bg-gray-50 rounded-xl p-4">
                                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Vehicle</p>
                                <p className="font-semibold font-mono text-gray-900">{invoice.vehicleRego || '—'}</p>
                                {invoice.vehicleDescription && (
                                    <p className="text-sm text-gray-500 mt-0.5">{invoice.vehicleDescription}</p>
                                )}
                            </div>
                        </div>

                        {/* 🔑 No job. Every MIGRATED invoice is in this state — the import
                            creates no jobs on purpose — so this is the common case here,
                            not an edge one. It is also the only way the Vehicle box above
                            ever fills for such an invoice, since the vehicle is read back
                            through the job. */}
                        {!invoice.jobUuid && invoice.status !== 'VOID' && (
                            <div className="border border-indigo-200 bg-indigo-50/50 rounded-xl p-4">
                                {!showCreateJob ? (
                                    <div className="flex items-center justify-between gap-3">
                                        <p className="text-sm text-gray-700">
                                            No job is linked to this invoice.
                                            <span className="text-gray-500"> Raise one to schedule or reopen the work.</span>
                                        </p>
                                        <button type="button" onClick={openCreateJob}
                                            className="shrink-0 text-xs font-semibold bg-indigo-600 text-white px-3 py-1.5 rounded-lg hover:bg-indigo-700">
                                            Create job
                                        </button>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        <p className="text-sm font-semibold text-gray-900">
                                            Which vehicle was this work done on?
                                        </p>

                                        {vehicles === null && (
                                            <p className="text-xs text-gray-500">Loading vehicles…</p>
                                        )}

                                        {vehicles !== null && vehicles.length > 0 && (
                                            <div className="space-y-1.5">
                                                {vehicles.map((v) => (
                                                    <label key={v.uuid} className="flex items-center gap-2 text-sm cursor-pointer">
                                                        <input type="radio" name="createJobVehicle"
                                                            checked={chosenVehicle === v.uuid}
                                                            onChange={() => { setChosenVehicle(v.uuid); setNewRego(''); }} />
                                                        <span className="font-mono">{v.regoNumber || '—'}</span>
                                                        <span className="text-gray-500">{v.displayName}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        )}

                                        {/* Always offered, not only when the list is empty:
                                            most imported customers appear on invoices but
                                            never on a job, so they own no vehicle at all and
                                            typing the rego IS the normal path. */}
                                        <div>
                                            <label className="block text-xs font-medium text-gray-600 mb-1">
                                                {vehicles?.length ? 'Or add another by registration' : 'Add the vehicle by registration'}
                                            </label>
                                            <input type="text" value={newRego}
                                                onChange={(e) => { setNewRego(e.target.value.toUpperCase()); setChosenVehicle(''); }}
                                                placeholder="E.G. ABC123"
                                                className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm font-mono uppercase focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none" />
                                            <p className="text-[11px] text-gray-500 mt-1">
                                                Make and model can be filled in later — the rego is enough.
                                            </p>
                                        </div>

                                        {jobError && (
                                            <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-2">{jobError}</p>
                                        )}

                                        <div className="flex items-center gap-2">
                                            <button type="button" onClick={submitCreateJob}
                                                disabled={jobBusy || (!chosenVehicle && !newRego.trim())}
                                                className="text-xs font-semibold bg-indigo-600 text-white px-3 py-1.5 rounded-lg hover:bg-indigo-700 disabled:opacity-50">
                                                {jobBusy ? 'Creating…' : 'Create job'}
                                            </button>
                                            <button type="button" onClick={() => { setShowCreateJob(false); setJobError(null); }}
                                                className="text-xs text-gray-500 hover:text-gray-700">
                                                Cancel
                                            </button>
                                        </div>
                                        <p className="text-[11px] text-gray-500">
                                            Each priced line becomes a work item. Lines at $0.00 do not —
                                            they were notes, not work.
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Invoice Header Fields */}
                        <div className="border border-gray-200 rounded-xl overflow-hidden">
                            <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
                                <span className="text-sm font-semibold text-gray-700">Invoice Details</span>
                                {isEditable && !editingHeader && (
                                    <button onClick={() => setEditingHeader(true)} className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700 font-medium">
                                        <Edit2 size={12} /> Edit
                                    </button>
                                )}
                                {editingHeader && (
                                    <div className="flex items-center gap-2">
                                        <button onClick={() => setEditingHeader(false)} className="text-xs text-gray-500 hover:text-gray-700">Cancel</button>
                                        <button onClick={handleHeaderSave} disabled={operationLoading}
                                                className="flex items-center gap-1 text-xs bg-indigo-600 text-white px-2 py-1 rounded-lg hover:bg-indigo-700 disabled:opacity-50">
                                            {operationLoading ? <RefreshCw size={11} className="animate-spin" /> : <Save size={11} />} Save
                                        </button>
                                    </div>
                                )}
                            </div>
                            <div className="p-4 grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-medium text-gray-500 mb-1">Issue Date</label>
                                    {editingHeader ? (
                                        <input type="date" value={headerForm.issueDate} onChange={e => setHeaderForm(f => ({ ...f, issueDate: e.target.value }))}
                                               className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none" />
                                    ) : (
                                        <p className="text-sm text-gray-900">{formatDate(invoice.issueDate)}</p>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-500 mb-1">Due Date</label>
                                    {editingHeader ? (
                                        <input type="date" value={headerForm.dueDate} onChange={e => setHeaderForm(f => ({ ...f, dueDate: e.target.value }))}
                                               className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none" />
                                    ) : (
                                        <p className="text-sm text-gray-900">{formatDate(invoice.dueDate)}</p>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-500 mb-1">Estimated Value</label>
                                    {editingHeader ? (
                                        <div className="relative">
                                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                                            <input type="number" step="0.01" value={headerForm.estimatedValue} onChange={e => setHeaderForm(f => ({ ...f, estimatedValue: e.target.value }))}
                                                   placeholder="0.00" className="w-full pl-7 pr-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none" />
                                        </div>
                                    ) : (
                                        <p className="text-sm text-gray-900">{invoice.estimatedValue ? formatCurrency(invoice.estimatedValue) : '—'}</p>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-500 mb-1">Created By</label>
                                    <p className="text-sm text-gray-900">
                                        {headerForm.claimCreatedBy
                                            ? <span className="text-indigo-700">{myName || 'You'}</span>
                                            : (invoice.createdByName || '—')}
                                        {alreadyMine && (
                                            <span className="ml-1 text-xs text-gray-500">
                                                — already you
                                            </span>
                                        )}
                                    </p>
                                    {/* 🔑 Only while editing, and only as "me". A migrated
                                        invoice is attributed to whoever ran the import —
                                        true of the row, useless to somebody asking whose
                                        invoice this is. The server reads the signed-in
                                        user, so this can say "mine" and never "Bob's".

                                        ⚠️ The label cannot show the name: the token
                                        carries staffUuid and email, never a name — which
                                        is why the sidebar shows an email address. The
                                        NAME comes back on the saved invoice, from the
                                        staff record, which is the whole point. */}
                                    {editingHeader && !headerForm.claimCreatedBy && (
                                        <button
                                            type="button"
                                            onClick={handleClaimCreatedBy}
                                            className="mt-1 text-xs font-medium text-indigo-600 hover:text-indigo-700"
                                        >
                                            Set to me
                                        </button>
                                    )}
                                    {editingHeader && headerForm.claimCreatedBy && !alreadyMine && (
                                        <button
                                            type="button"
                                            onClick={() => setHeaderForm(f => ({ ...f, claimCreatedBy: false }))}
                                            className="mt-1 text-xs text-gray-500 hover:text-gray-700"
                                        >
                                            Undo
                                        </button>
                                    )}
                                </div>
                                <div className="col-span-2">
                                    <label className="block text-xs font-medium text-gray-500 mb-1">Notes</label>
                                    {editingHeader ? (
                                        <textarea value={headerForm.notes} onChange={e => setHeaderForm(f => ({ ...f, notes: e.target.value }))}
                                                  rows={3} placeholder="Add notes about this invoice…"
                                                  className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none resize-none" />
                                    ) : (
                                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{invoice.notes || <span className="text-gray-400">No notes</span>}</p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Line Items */}
                        <div className="border border-gray-200 rounded-xl overflow-hidden">
                            <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
                                <span className="text-sm font-semibold text-gray-700">
                                    Line Items <span className="ml-2 text-xs font-normal text-gray-400">({lineItems.length})</span>
                                </span>
                                {canEditLineItems && (
                                    <button onClick={() => setShowAddForm(v => !v)} className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700 font-medium">
                                        <Plus size={12} /> Add Adjustment
                                    </button>
                                )}
                            </div>

                            {showAddForm && canEditLineItems && (
                                <div className="p-4 bg-indigo-50 border-b border-indigo-100 space-y-3">
                                    <p className="text-xs font-semibold text-indigo-700 uppercase tracking-wide flex items-center gap-1">
                                        <Plus size={11} /> New Line Item / Adjustment
                                    </p>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
                                            <select value={addForm.lineType} onChange={e => setAddForm(f => ({ ...f, lineType: e.target.value }))}
                                                    className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none">
                                                {lineTypeOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-gray-600 mb-1">Quantity</label>
                                            <input type="number" step="0.01" min="0.01" value={addForm.quantity} onChange={e => setAddForm(f => ({ ...f, quantity: e.target.value }))}
                                                   className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none" />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="col-span-2 sm:col-span-1">
                                            <label className="block text-xs font-medium text-gray-600 mb-1">Description <span className="text-red-400">*</span></label>
                                            <input type="text" value={addForm.description} onChange={e => setAddForm(f => ({ ...f, description: e.target.value }))}
                                                   placeholder="e.g. Diagnostic fee adjustment"
                                                   className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none" />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-gray-600 mb-1">Unit Price (ex GST) <span className="text-red-400">*</span></label>
                                            <div className="relative">
                                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                                                <input type="number" step="0.01" value={addForm.unitPrice} onChange={e => setAddForm(f => ({ ...f, unitPrice: e.target.value }))}
                                                       placeholder="0.00" className="w-full pl-7 pr-3 py-1.5 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none" />
                                            </div>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-600 mb-1 flex items-center gap-1">
                                            <MessageSquare size={11} /> Internal Comment (optional)
                                        </label>
                                        <textarea value={addForm.comment} onChange={e => setAddForm(f => ({ ...f, comment: e.target.value }))}
                                                  rows={2} placeholder="Reason for this adjustment…"
                                                  className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none resize-none" />
                                    </div>
                                    {addFormTotal > 0 && (
                                        <p className="text-xs text-indigo-700 font-medium">
                                            Line total: {formatCurrency(addFormTotal)} + GST = {formatCurrency(addFormTotal * 1.1)}
                                        </p>
                                    )}
                                    <div className="flex items-center justify-end gap-2 pt-1">
                                        <button onClick={() => setShowAddForm(false)} className="text-xs text-gray-500 hover:text-gray-700 px-3 py-1.5 rounded-lg hover:bg-white transition-colors">Cancel</button>
                                        <button onClick={handleAddLineItem} disabled={operationLoading || !addForm.description.trim() || !addForm.unitPrice}
                                                className="flex items-center gap-1 text-xs bg-indigo-600 text-white px-3 py-1.5 rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50">
                                            {operationLoading ? <RefreshCw size={11} className="animate-spin" /> : <Plus size={11} />} Add Line Item
                                        </button>
                                    </div>
                                </div>
                            )}

                            {lineItems.length === 0 ? (
                                <div className="px-4 py-8 text-center text-gray-400 text-sm">
                                    No line items on this invoice yet
                                    {canEditLineItems && <p className="mt-1 text-xs">Use "Add Adjustment" to add items manually</p>}
                                </div>
                            ) : (
                                <table className="w-full text-sm">
                                    <thead>
                                    <tr className="border-b border-gray-100">
                                        <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 w-24">Type</th>
                                        <th className="text-left px-4 py-2 text-xs font-medium text-gray-500">Description</th>
                                        <th className="text-right px-4 py-2 text-xs font-medium text-gray-500 w-16">Qty</th>
                                        <th className="text-right px-4 py-2 text-xs font-medium text-gray-500 w-24">Unit</th>
                                        <th className="text-right px-4 py-2 text-xs font-medium text-gray-500 w-24">Total</th>
                                        {canEditLineItems && <th className="w-16 px-4 py-2" />}
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {lineItems.map((li) => {
                                        const isEditing = editingLineItem === li.uuid;
                                        const isSaving = savingLineItem === li.uuid;
                                        const liveTotal = (parseFloat(lineItemForm.quantity) || 0) * (parseFloat(lineItemForm.unitPrice) || 0);
                                        const canSave = lineItemForm.description?.trim() && parseFloat(lineItemForm.unitPrice) > 0 && parseFloat(lineItemForm.quantity) > 0;
                                        return (
                                            <tr key={li.uuid} className="group border-b border-gray-50 hover:bg-gray-50/50">
                                                <td className="px-4 py-2">
                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">{getLineTypeLabel(li.lineType, lineTypeOptions)}</span>
                                                </td>
                                                <td className="px-4 py-2">
                                                    {isEditing ? (
                                                        <input autoFocus type="text" value={lineItemForm.description}
                                                               onChange={e => setLineItemForm(f => ({ ...f, description: e.target.value }))}
                                                               onKeyDown={e => handleLineItemKeyDown(e, li)} disabled={isSaving}
                                                               className="w-full px-2 py-1 border border-indigo-300 rounded text-sm focus:ring-1 focus:ring-indigo-500 outline-none disabled:opacity-60" />
                                                    ) : (
                                                        <span className="text-gray-800">{li.description}</span>
                                                    )}
                                                    {li.jobWorkItemUuid && <span className="ml-2 text-xs text-gray-400 italic">auto</span>}
                                                </td>
                                                <td className="px-4 py-2 text-right">
                                                    {isEditing ? (
                                                        <input type="number" step="0.01" min="0.01" value={lineItemForm.quantity}
                                                               onChange={e => setLineItemForm(f => ({ ...f, quantity: e.target.value }))}
                                                               onKeyDown={e => handleLineItemKeyDown(e, li)} disabled={isSaving}
                                                               className="w-16 px-2 py-1 border border-indigo-300 rounded text-sm text-right focus:ring-1 focus:ring-indigo-500 outline-none disabled:opacity-60" />
                                                    ) : (
                                                        <span className="text-gray-700">{li.quantity}</span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-2 text-right">
                                                    {isEditing ? (
                                                        <div className="relative inline-flex items-center">
                                                            <span className="absolute left-2 text-gray-400 text-xs">$</span>
                                                            <input type="number" step="0.01" min="0.01" value={lineItemForm.unitPrice}
                                                                   onChange={e => setLineItemForm(f => ({ ...f, unitPrice: e.target.value }))}
                                                                   onKeyDown={e => handleLineItemKeyDown(e, li)} disabled={isSaving}
                                                                   className="w-24 pl-5 pr-2 py-1 border border-indigo-300 rounded text-sm text-right focus:ring-1 focus:ring-indigo-500 outline-none disabled:opacity-60" />
                                                        </div>
                                                    ) : (
                                                        <span className="text-gray-700">{formatCurrency(li.unitPrice)}</span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-2 text-right font-medium text-gray-900">
                                                    {isEditing ? <span className="text-indigo-600">{formatCurrency(liveTotal)}</span> : formatCurrency(li.lineTotal)}
                                                </td>
                                                {canEditLineItems && (
                                                    <td className="px-4 py-2">
                                                        {isEditing ? (
                                                            <div className="flex items-center gap-1">
                                                                <button onClick={() => handleSaveLineItem(li)} disabled={isSaving || !canSave} title="Save (Enter)"
                                                                        className="p-1 text-indigo-600 hover:bg-indigo-50 rounded transition-colors disabled:opacity-40">
                                                                    {isSaving ? <RefreshCw size={13} className="animate-spin" /> : <Save size={13} />}
                                                                </button>
                                                                <button onClick={handleCancelEditLineItem} disabled={isSaving} title="Cancel (Escape)"
                                                                        className="p-1 text-gray-400 hover:bg-gray-100 rounded transition-colors disabled:opacity-40">
                                                                    <X size={13} />
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                                <button onClick={() => handleStartEditLineItem(li)} title="Edit line item"
                                                                        className="p-1 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors">
                                                                    <Edit2 size={13} />
                                                                </button>
                                                                <button onClick={() => handleDeleteLineItem(li)} disabled={operationLoading} title="Remove line item"
                                                                        className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-40">
                                                                    <Trash2 size={13} />
                                                                </button>
                                                            </div>
                                                        )}
                                                    </td>
                                                )}
                                            </tr>
                                        );
                                    })}
                                    </tbody>
                                </table>
                            )}
                        </div>

                        {/* Totals */}
                        <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                            <div className="flex justify-between text-sm text-gray-600"><span>Subtotal (ex GST)</span><span>{formatCurrency(invoice.subtotal)}</span></div>
                            <div className="flex justify-between text-sm text-gray-600"><span>GST (10%)</span><span>{formatCurrency(invoice.gstAmount)}</span></div>
                            <div className="flex justify-between text-sm font-semibold text-gray-900 pt-2 border-t border-gray-200"><span>Total</span><span>{formatCurrency(invoice.totalAmount)}</span></div>
                            {invoice.amountPaid > 0 && (
                                <>
                                    <div className="flex justify-between text-sm text-emerald-600"><span>Amount Paid</span><span>−{formatCurrency(invoice.amountPaid)}</span></div>
                                    <div className="flex justify-between text-sm font-bold text-gray-900 pt-2 border-t border-gray-200">
                                        <span>Balance Due</span>
                                        <span className={invoice.balanceDue > 0 ? 'text-red-600' : 'text-emerald-600'}>{formatCurrency(invoice.balanceDue)}</span>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Timestamps */}
                        <div className="grid grid-cols-2 gap-3 text-xs text-gray-400">
                            <div><span className="font-medium">Created:</span> {formatDateTime(invoice.createdAt)}</div>
                            <div><span className="font-medium">Updated:</span> {formatDateTime(invoice.updatedAt)}</div>
                            {invoice.sentAt && <div><span className="font-medium">Sent:</span> {formatDateTime(invoice.sentAt)}</div>}
                            {invoice.paidAt && <div><span className="font-medium">Paid:</span> {formatDateTime(invoice.paidAt)}</div>}
                        </div>
                    </div>

                    {/* ---- Sub-modals ---- */}

                    {/* Send Confirm */}
                    {showSendLocal && (
                        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60]">
                            <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
                                <h4 className="text-base font-semibold mb-2">Send Invoice</h4>
                                <p className="text-sm text-gray-600 mb-5">
                                    Send <span className="font-mono font-medium">{invoice.invoiceNumber}</span> to{' '}
                                    <span className="font-medium">{invoice.customerName}</span>?
                                    {invoice.customerEmail && <span className="block text-xs text-gray-400 mt-1">Email: {invoice.customerEmail}</span>}
                                </p>
                                <p className="text-xs text-gray-400 mb-5 bg-gray-50 p-3 rounded-lg">
                                    A PDF will be generated and attached to the email automatically.
                                </p>
                                <div className="flex justify-end gap-2">
                                    <button onClick={() => setShowSendLocal(false)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
                                    <button onClick={handleSendInvoice} disabled={operationLoading}
                                            className="flex items-center gap-1.5 px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">
                                        {operationLoading ? <RefreshCw size={14} className="animate-spin" /> : <Send size={14} />} Send
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Record Payment */}
                    {showPaymentLocal && (
                        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60]">
                            <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
                                <div className="flex items-center justify-between mb-4">
                                    <h4 className="text-base font-semibold">Record Payment</h4>
                                    <button onClick={() => setShowPaymentLocal(false)} className="p-1 hover:bg-gray-100 rounded-lg"><X size={18} /></button>
                                </div>
                                <div className="space-y-3">
                                    <div>
                                        <label className="block text-xs font-medium text-gray-600 mb-1">Amount <span className="text-red-400">*</span></label>
                                        <div className="relative">
                                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                                            <input type="number" step="0.01" min="0" value={paymentForm.amount}
                                                   onChange={e => setPaymentForm(f => ({ ...f, amount: e.target.value }))}
                                                   placeholder={formatCurrency(invoice.balanceDue).replace('$', '')}
                                                   className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none" />
                                        </div>
                                        {invoice.balanceDue > 0 && <p className="text-xs text-gray-400 mt-1">Balance due: {formatCurrency(invoice.balanceDue)}</p>}
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-600 mb-1">Payment Method <span className="text-red-400">*</span></label>
                                        <select value={paymentForm.paymentMethod} onChange={e => setPaymentForm(f => ({ ...f, paymentMethod: e.target.value }))}
                                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none bg-white">
                                            {paymentMethodOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
                                        <input type="text" value={paymentForm.notes} onChange={e => setPaymentForm(f => ({ ...f, notes: e.target.value }))}
                                               placeholder="Optional payment note"
                                               className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none" />
                                    </div>
                                </div>
                                <div className="flex justify-end gap-2 mt-5">
                                    <button onClick={() => setShowPaymentLocal(false)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
                                    <button onClick={handleRecordPayment}
                                            disabled={operationLoading || (paymentForm.paymentMethod !== 'TRADE_CREDIT' && !paymentForm.amount)}
                                            className="flex items-center gap-1.5 px-4 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50">
                                        {operationLoading ? <RefreshCw size={14} className="animate-spin" /> : <CreditCard size={14} />} Record Payment
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Void Confirm */}
                    {showVoidLocal && (
                        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60]">
                            <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
                                <div className="flex items-center justify-between mb-3">
                                    <h4 className="text-base font-semibold text-red-600">Void Invoice</h4>
                                    <button onClick={() => setShowVoidLocal(false)} className="p-1 hover:bg-gray-100 rounded-lg"><X size={18} /></button>
                                </div>
                                <p className="text-sm text-gray-600 mb-3">Void <span className="font-mono font-medium">{invoice.invoiceNumber}</span>? This cannot be undone.</p>
                                <textarea value={voidReason} onChange={e => setVoidReason(e.target.value)}
                                          placeholder="Reason (optional)…" rows={3}
                                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500/20 focus:border-red-400 outline-none resize-none mb-4" />
                                <div className="flex justify-end gap-2">
                                    <button onClick={() => setShowVoidLocal(false)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
                                    <button onClick={handleVoidInvoice} disabled={operationLoading}
                                            className="flex items-center gap-1.5 px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">
                                        {operationLoading ? <RefreshCw size={14} className="animate-spin" /> : <Ban size={14} />} Void Invoice
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {showReconcileLocal && (
                        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60]">
                            <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
                                <div className="flex items-center justify-between mb-3">
                                    <h4 className="text-base font-semibold text-teal-700">Reconcile Invoice</h4>
                                    <button onClick={() => setShowReconcileLocal(false)} className="p-1 hover:bg-gray-100 rounded-lg"><X size={18} /></button>
                                </div>
                                {/* Says what actually happens: this is a write to the
                                    workshop's books, not just a status change here. */}
                                <p className="text-sm text-gray-600 mb-4">
                                    Record <span className="font-mono font-medium">{invoice.invoiceNumber}</span> in
                                    your accounting software? The invoice and its payment are sent across, then it
                                    moves to the Reconciled tab.
                                </p>
                                <div className="flex justify-end gap-2">
                                    <button onClick={() => setShowReconcileLocal(false)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
                                    <button onClick={handleReconcileInvoice} disabled={operationLoading}
                                            className="flex items-center gap-1.5 px-4 py-2 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50">
                                        {operationLoading ? <RefreshCw size={14} className="animate-spin" /> : <Archive size={14} />} Reconcile
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Delete Line Item Confirm */}
                    {deleteTarget && (
                        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60]">
                            <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
                                <div className="flex items-start gap-3 mb-4">
                                    <div className="w-9 h-9 bg-red-100 rounded-xl flex items-center justify-center shrink-0">
                                        <Trash2 size={16} className="text-red-600" />
                                    </div>
                                    <div>
                                        <h4 className="text-base font-semibold text-gray-900">Remove Line Item</h4>
                                        <p className="text-sm text-gray-500 mt-0.5">This will remove the charge from the invoice.</p>
                                    </div>
                                </div>
                                <div className="bg-gray-50 rounded-lg px-4 py-3 mb-5">
                                    <p className="text-sm text-gray-700 font-medium truncate">{deleteTarget.description}</p>
                                </div>
                                <div className="flex justify-end gap-2">
                                    <button
                                        onClick={() => setDeleteTarget(null)}
                                        disabled={operationLoading}
                                        className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleConfirmDelete}
                                        disabled={operationLoading}
                                        className="flex items-center gap-1.5 px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                                    >
                                        {operationLoading
                                            ? <RefreshCw size={14} className="animate-spin" />
                                            : <Trash2 size={14} />
                                        }
                                        Remove
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// ============================================================
// ArchiveTab — Reconciled invoices synced to accounting
// ============================================================

/** Returns { dateFrom: 'YYYY-MM-DD', dateTo: 'YYYY-MM-DD' } for the current month. */
const getCurrentMonthRange = () => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const lastDay = new Date(y, now.getMonth() + 1, 0).getDate();
    return {
        dateFrom: `${y}-${m}-01`,
        dateTo: `${y}-${m}-${String(lastDay).padStart(2, '0')}`,
    };
};

const ArchiveTab = () => {
    const dispatch = useDispatch();
    const {
        archive = [],
        archivePagination = { page: 0, size: 20, totalElements: 0, totalPages: 0 },
        archiveTotalAmount = 0,
        archiveLoading = false,
        archiveError = null,
    } = useSelector(state => state.invoices) || {};

    const [archiveSearch, setArchiveSearch] = useState('');
    const [activeSearch, setActiveSearch] = useState('');
    const [selectedArchiveEntry, setSelectedArchiveEntry] = useState(null);

    // Date range — default to current month
    const defaultRange = useMemo(() => getCurrentMonthRange(), []);
    const [dateFrom, setDateFrom] = useState(defaultRange.dateFrom);
    const [dateTo, setDateTo] = useState(defaultRange.dateTo);
    const [activeDateFrom, setActiveDateFrom] = useState(defaultRange.dateFrom);
    const [activeDateTo, setActiveDateTo] = useState(defaultRange.dateTo);

    /** Dispatch the right thunk based on current search + date state. */
    const dispatchArchiveFetch = useCallback(({ query = '', from = '', to = '', page = 0 } = {}) => {
        if (query) {
            dispatch(searchInvoiceArchive({ query, dateFrom: from, dateTo: to, page, size: 20 }));
        } else {
            dispatch(fetchInvoiceArchive({ dateFrom: from, dateTo: to, page, size: 20 }));
        }
    }, [dispatch]);

    // Initial load with current-month date range
    useEffect(() => {
        dispatchArchiveFetch({ from: activeDateFrom, to: activeDateTo, page: 0 });
    }, [dispatch]);

    // Re-fetch on page change
    useEffect(() => {
        dispatchArchiveFetch({
            query: activeSearch,
            from: activeDateFrom,
            to: activeDateTo,
            page: archivePagination.page,
        });
    }, [archivePagination.page]);

    const handleSearch = useCallback(() => {
        const q = archiveSearch.trim();
        setActiveSearch(q);
        setActiveDateFrom(dateFrom);
        setActiveDateTo(dateTo);
        dispatch(setArchivePage(0));
        dispatchArchiveFetch({ query: q, from: dateFrom, to: dateTo, page: 0 });
    }, [dispatch, archiveSearch, dateFrom, dateTo, dispatchArchiveFetch]);

    const handleClearSearch = useCallback(() => {
        setArchiveSearch('');
        setActiveSearch('');
        const range = getCurrentMonthRange();
        setDateFrom(range.dateFrom);
        setDateTo(range.dateTo);
        setActiveDateFrom(range.dateFrom);
        setActiveDateTo(range.dateTo);
        dispatch(setArchivePage(0));
        dispatchArchiveFetch({ from: range.dateFrom, to: range.dateTo, page: 0 });
    }, [dispatch, dispatchArchiveFetch]);

    const handleSearchKeyDown = (e) => {
        if (e.key === 'Enter') handleSearch();
        if (e.key === 'Escape') handleClearSearch();
    };

    const hasActiveDateFilter = activeDateFrom || activeDateTo;

    const formatSyncedAt = (ts) => {
        if (!ts) return '—';
        return new Date(ts).toLocaleString('en-AU', {
            day: '2-digit', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
        });
    };

    return (
        <div className="bg-white rounded-2xl border border-gray-200 flex flex-col" style={{ minHeight: 'calc(100vh - 320px)' }}>
            {/* Toolbar — single row: dates + text search + actions */}
            <div className="p-4 border-b border-gray-100">
                <div className="flex flex-wrap items-end gap-3">
                    {/* Date From */}
                    <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1 flex items-center gap-1">
                            <Calendar size={11} /> From
                        </label>
                        <input
                            type="date"
                            value={dateFrom}
                            onChange={(e) => setDateFrom(e.target.value)}
                            onKeyDown={handleSearchKeyDown}
                            className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none"
                        />
                    </div>
                    {/* Date To */}
                    <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1 flex items-center gap-1">
                            <Calendar size={11} /> To
                        </label>
                        <input
                            type="date"
                            value={dateTo}
                            onChange={(e) => setDateTo(e.target.value)}
                            onKeyDown={handleSearchKeyDown}
                            className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none"
                        />
                    </div>
                    {/* Text search */}
                    <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1 flex items-center gap-1">
                            <Search size={11} /> Invoice / Job #
                        </label>
                        <div className="relative">
                            <input
                                type="text"
                                placeholder="e.g. INV-0042 or JOB-18"
                                value={archiveSearch}
                                onChange={(e) => setArchiveSearch(e.target.value)}
                                onKeyDown={handleSearchKeyDown}
                                className="px-3 pr-8 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none w-52"
                            />
                            {archiveSearch && (
                                <button onClick={() => setArchiveSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                                    <X size={14} />
                                </button>
                            )}
                        </div>
                    </div>
                    {/* Search button */}
                    <button onClick={handleSearch}
                            className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors">
                        <Search size={14} /> Search
                    </button>
                    {/* Clear */}
                    {(activeSearch || hasActiveDateFilter) && (
                        <button onClick={handleClearSearch}
                                className="px-3 py-2 text-gray-600 hover:bg-gray-200 rounded-lg text-sm transition-colors">
                            Clear
                        </button>
                    )}
                    {/* Refresh */}
                    <button onClick={() => {
                        dispatch(setArchivePage(0));
                        dispatchArchiveFetch({ query: activeSearch, from: activeDateFrom, to: activeDateTo, page: 0 });
                    }} className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" title="Refresh">
                        <RefreshCw size={16} className={archiveLoading ? 'animate-spin' : ''} />
                    </button>

                    {/* Inline total */}
                    <div className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 rounded-lg ml-auto">
                        <DollarSign size={14} className="text-emerald-600" />
                        <span className="text-xs text-emerald-600 font-medium">Total</span>
                        <span className="text-sm font-bold text-emerald-700">{formatCurrency(archiveTotalAmount)}</span>
                        <span className="text-xs text-emerald-500">({archivePagination.totalElements})</span>
                    </div>
                </div>
                {/* Active filter summary */}
                {(activeSearch || hasActiveDateFilter) && (
                    <p className="text-xs text-indigo-600 mt-2">
                        {activeSearch && <>Searching "{activeSearch}" · </>}
                        {activeDateFrom && activeDateTo && <>Synced {activeDateFrom} — {activeDateTo}</>}
                        {activeDateFrom && !activeDateTo && <>Synced from {activeDateFrom}</>}
                        {!activeDateFrom && activeDateTo && <>Synced up to {activeDateTo}</>}
                    </p>
                )}
            </div>

            {/* Loading */}
            {archiveLoading && (
                <div className="flex flex-col items-center justify-center py-16 flex-1 gap-3">
                    <div className="w-10 h-10 rounded-full border-4 border-indigo-100 border-t-indigo-600 animate-spin" />
                    <p className="text-sm text-gray-500 font-medium">Loading archive…</p>
                </div>
            )}

            {/* Error */}
            {archiveError && !archiveLoading && (
                <div className="flex items-center justify-center py-12 text-red-500 flex-1">
                    <AlertTriangle size={24} /><span className="ml-2">{archiveError}</span>
                </div>
            )}

            {/* Table */}
            {!archiveLoading && !archiveError && (
                <div className="p-4 flex-1 overflow-auto">
                    {archive.length === 0 ? (
                        <div className="text-center py-12 text-gray-500 text-sm">
                            {activeSearch ? 'No archive entries match your search' : 'No reconciled invoices for this period'}
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                <tr className="border-b border-gray-100">
                                    <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice #</th>
                                    <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Job #</th>
                                    <th className="text-right p-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                                    <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Synced At</th>
                                    <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Software</th>
                                    <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase tracking-wider">External Ref</th>
                                    <th className="w-12" />
                                </tr>
                                </thead>
                                <tbody>
                                {archive.map((entry) => (
                                    <tr key={entry.uuid}
                                        onClick={() => setSelectedArchiveEntry(entry)}
                                        className="border-b border-gray-50 hover:bg-indigo-50/30 transition-colors cursor-pointer">
                                        <td className="p-4">
                                            <span className="font-mono text-sm font-medium text-indigo-600">{entry.invoiceNumber}</span>
                                        </td>
                                        <td className="p-4">
                                            <span className="font-mono text-sm text-gray-600">{entry.jobNumber}</span>
                                        </td>
                                        <td className="p-4 text-right">
                                            <span className="font-semibold text-gray-900">{formatCurrency(entry.invoiceAmount)}</span>
                                        </td>
                                        <td className="p-4 text-sm text-gray-600">{formatSyncedAt(entry.syncedAt)}</td>
                                        <td className="p-4">
                                            {entry.integrationIdentifiers?.software ? (
                                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-teal-100 text-teal-700">
                                                    {entry.integrationIdentifiers.software}
                                                </span>
                                            ) : (
                                                <span className="text-gray-400 text-sm">—</span>
                                            )}
                                        </td>
                                        <td className="p-4">
                                            {entry.integrationIdentifiers?.externalInvoiceId ? (
                                                <span className="font-mono text-xs text-gray-500 truncate block max-w-[180px]" title={entry.integrationIdentifiers.externalInvoiceId}>
                                                    {entry.integrationIdentifiers.externalInvoiceId.substring(0, 12)}…
                                                </span>
                                            ) : (
                                                <span className="text-gray-400 text-sm">—</span>
                                            )}
                                        </td>
                                        <td className="p-4">
                                            <button
                                                onClick={(e) => { e.stopPropagation(); setSelectedArchiveEntry(entry); }}
                                                className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                                                title="View details"
                                            >
                                                <Eye size={16} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* Pagination */}
            {!archiveLoading && !archiveError && archive.length > 0 && (
                <div className="border-t border-gray-100 px-4 py-3 flex items-center justify-between">
                    <p className="text-sm text-gray-500">
                        {archivePagination.totalElements} reconciled invoice{archivePagination.totalElements !== 1 ? 's' : ''}
                    </p>
                    <div className="flex items-center gap-2">
                        <button onClick={() => dispatch(setArchivePage(archivePagination.page - 1))} disabled={archivePagination.page === 0}
                                className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed">
                            <ChevronLeft size={16} /> Previous
                        </button>
                        <span className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-lg">{archivePagination.page + 1}</span>
                        <span className="text-sm text-gray-500">of {archivePagination.totalPages || 1}</span>
                        <button onClick={() => dispatch(setArchivePage(archivePagination.page + 1))} disabled={archivePagination.page >= archivePagination.totalPages - 1}
                                className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed">
                            Next <ChevronRight size={16} />
                        </button>
                    </div>
                </div>
            )}

            {/* Archive Entry Detail Dialog */}
            {selectedArchiveEntry && (
                <>
                    <div className="fixed inset-0 bg-black/30 z-40" onClick={() => setSelectedArchiveEntry(null)} />
                    <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl overflow-hidden">
                            {/* Header */}
                            <div className="flex items-start justify-between p-6 border-b border-gray-100">
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center shrink-0">
                                        <Archive size={24} className="text-teal-600" />
                                    </div>
                                    <div>
                                        <p className="font-mono text-xl font-bold text-gray-900">{selectedArchiveEntry.invoiceNumber}</p>
                                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-700 mt-1">
                                            <CheckCircle size={10} /> Reconciled
                                        </span>
                                    </div>
                                </div>
                                <button onClick={() => setSelectedArchiveEntry(null)} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 transition-colors">
                                    <X size={20} />
                                </button>
                            </div>

                            {/* Body */}
                            <div className="p-6 space-y-5">
                                {/* Top row — 4 columns */}
                                <div className="grid grid-cols-4 gap-4">
                                    <div className="bg-gray-50 rounded-xl p-4">
                                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Invoice Amount</p>
                                        <p className="text-2xl font-bold text-gray-900">{formatCurrency(selectedArchiveEntry.invoiceAmount)}</p>
                                    </div>
                                    <div className="bg-gray-50 rounded-xl p-4">
                                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Job Number</p>
                                        <p className="font-mono text-xl font-semibold text-gray-900">{selectedArchiveEntry.jobNumber || '—'}</p>
                                    </div>
                                    <div className="bg-gray-50 rounded-xl p-4">
                                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Synced At</p>
                                        <p className="text-sm font-medium text-gray-900 mt-1">{formatSyncedAt(selectedArchiveEntry.syncedAt)}</p>
                                    </div>
                                    <div className="bg-gray-50 rounded-xl p-4">
                                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Software</p>
                                        {selectedArchiveEntry.integrationIdentifiers?.software ? (
                                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-sm font-medium bg-teal-100 text-teal-700 mt-1">
                                                {selectedArchiveEntry.integrationIdentifiers.software}
                                            </span>
                                        ) : (
                                            <p className="text-sm text-gray-400 mt-1">Manual</p>
                                        )}
                                    </div>
                                </div>

                                {/* Created At */}
                                {selectedArchiveEntry.createdAt && (
                                    <div className="bg-gray-50 rounded-xl p-4">
                                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Created At</p>
                                        <p className="text-sm font-medium text-gray-900">{formatSyncedAt(selectedArchiveEntry.createdAt)}</p>
                                    </div>
                                )}

                                {/* Integration details — full width IDs */}
                                {selectedArchiveEntry.integrationIdentifiers && (
                                    <div className="bg-gray-50 rounded-xl p-5 space-y-4">
                                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Integration Details</p>

                                        <div className="space-y-3">
                                            {selectedArchiveEntry.integrationIdentifiers.externalInvoiceId && (
                                                <div>
                                                    <p className="text-xs text-gray-500 mb-1">External Invoice ID</p>
                                                    <p className="font-mono text-sm text-gray-800 bg-white px-4 py-2.5 rounded-lg border border-gray-200 break-all select-all">
                                                        {selectedArchiveEntry.integrationIdentifiers.externalInvoiceId}
                                                    </p>
                                                </div>
                                            )}

                                            {selectedArchiveEntry.integrationIdentifiers.externalContactId && (
                                                <div>
                                                    <p className="text-xs text-gray-500 mb-1">External Contact ID</p>
                                                    <p className="font-mono text-sm text-gray-800 bg-white px-4 py-2.5 rounded-lg border border-gray-200 break-all select-all">
                                                        {selectedArchiveEntry.integrationIdentifiers.externalContactId}
                                                    </p>
                                                </div>
                                            )}

                                            {selectedArchiveEntry.integrationIdentifiers.externalPaymentId && (
                                                <div>
                                                    <p className="text-xs text-gray-500 mb-1">External Payment ID</p>
                                                    <p className="font-mono text-sm text-gray-800 bg-white px-4 py-2.5 rounded-lg border border-gray-200 break-all select-all">
                                                        {selectedArchiveEntry.integrationIdentifiers.externalPaymentId}
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {!selectedArchiveEntry.integrationIdentifiers && (
                                    <div className="bg-gray-50 rounded-xl p-4">
                                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Integration</p>
                                        <p className="text-sm text-gray-500">Manually reconciled — no external integration</p>
                                    </div>
                                )}
                            </div>

                            {/* Footer */}
                            <div className="px-6 pb-6 flex justify-end">
                                <button onClick={() => setSelectedArchiveEntry(null)}
                                        className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

// ============================================================
// InvoicesPage
// ============================================================


// ============================================================
// Income & Targets tab
// ============================================================

/**
 * Formats a percentage that the SERVER computed. Null means "not applicable" — either no
 * target is set for the month, or the target is zero and a percentage would be meaningless.
 * Rendered as an em dash rather than 0%, which would read as a total failure to sell.
 */
const formatPercent = (value) =>
    value === null || value === undefined ? '—' : `${Number(value).toFixed(1)}%`;

/** Signed currency, so a variance reads as -$5,600.00 rather than needing a legend. */
const formatSignedCurrency = (value) => {
    if (value === null || value === undefined) return '—';
    const n = Number(value);
    return `${n < 0 ? '-' : '+'}${formatCurrency(Math.abs(n))}`;
};

const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];

/** One headline figure. */
const StatTile = ({ label, value, sub = null, tone = 'default', icon: Icon = null }) => {
    const tones = {
        default: 'text-gray-900',
        positive: 'text-emerald-600',
        negative: 'text-red-600',
        muted: 'text-gray-500',
    };
    return (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-1.5 text-xs font-medium text-gray-500 uppercase tracking-wide">
                {Icon && <Icon size={13} />} {label}
            </div>
            <div className={`mt-1.5 text-2xl font-semibold tabular-nums ${tones[tone]}`}>{value}</div>
            {sub && <div className="mt-1 text-xs text-gray-500">{sub}</div>}
        </div>
    );
};

/**
 * "How are we doing this month" — actual invoiced income against the month's target.
 *
 * While the month is running the server also supplies a pro-rata target and a projection,
 * because a bare "we're at 40% of target" on the 12th is alarming for no reason. Once the
 * month is over those are null and only the real verdict is shown.
 */
/** Exported so the headline figure can be tested without standing up the page. */
export const MonthProgressCard = ({ performance, loading, onEditTarget }) => {
    if (loading && !performance) {
        return (
            <div className="bg-white rounded-2xl border border-gray-200 p-6 flex items-center gap-2 text-gray-500">
                <Loader size={16} className="animate-spin" /> Loading this month…
            </div>
        );
    }
    if (!performance) return null;

    const { income, inProgress, elapsedDays, daysInMonth, proRataTarget, onTrack, projectedTotalIncGst } = performance;
    const hasTarget = income.target !== null && income.target !== undefined;

    // Bar is capped at 100% so an outstanding month doesn't render past the container,
    // while the real figure stays visible in the percentage beside it.
    const pct = income.targetPercent === null || income.targetPercent === undefined
        ? 0
        : Math.min(Number(income.targetPercent), 100);

    return (
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                        <TrendingUp size={16} className="text-indigo-600" />
                        {income.label}
                        {inProgress && (
                            <span className="text-xs font-normal text-gray-500">
                                day {elapsedDays} of {daysInMonth}
                            </span>
                        )}
                    </div>
                    {/* ⚠️ PAID, not invoiced. This is the figure the target is judged on,
                        so the headline has to be the same money — a card reading
                        "invoiced" above a chart plotting "paid" would have the month
                        looking better here than it does there. */}
                    <div className="mt-2 text-3xl font-semibold text-gray-900 tabular-nums">
                        {formatCurrency(income.collected)}
                    </div>
                    <div className="mt-0.5 text-xs text-gray-500">
                        paid this month · {income.invoiceCount} invoice{income.invoiceCount === 1 ? '' : 's'}
                    </div>
                    {/* What is still out there to chase, and where the month lands if it
                        all comes in. Only shown when there is something to chase. */}
                    {Number(income.possibleIncGst) > Number(income.collected) && (
                        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                            {Number(income.outstanding) > 0 && (
                                <span className="text-amber-700">
                                    {formatCurrency(income.outstanding)} sent, not paid
                                </span>
                            )}
                            {Number(income.draftIncGst) > 0 && (
                                <span className="text-amber-600">
                                    {formatCurrency(income.draftIncGst)} in drafts
                                </span>
                            )}
                            <span className="font-medium text-gray-700">
                                {formatCurrency(income.possibleIncGst)} possible
                            </span>
                        </div>
                    )}
                </div>

                <button
                    onClick={onEditTarget}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50"
                >
                    <Target size={14} /> {hasTarget ? 'Change target' : 'Set target'}
                </button>
            </div>

            {!hasTarget ? (
                <div className="mt-4 text-sm text-gray-500 bg-gray-50 rounded-lg px-3 py-2.5">
                    No sales target set for {income.label}. Set one to track how the month is
                    tracking against where you want it.
                </div>
            ) : (
                <>
                    <div className="mt-4">
                        <div className="flex items-center justify-between text-xs text-gray-600 mb-1.5">
                            <span>{formatPercent(income.targetPercent)} of {formatCurrency(income.target)} target</span>
                            <span className={income.targetMet ? 'text-emerald-600 font-medium' : 'text-gray-500'}>
                                {formatSignedCurrency(income.targetVariance)}
                            </span>
                        </div>
                        <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                            <div
                                className={`h-full rounded-full transition-all ${
                                    income.targetMet ? 'bg-emerald-500'
                                        : onTrack ? 'bg-indigo-500' : 'bg-amber-500'
                                }`}
                                style={{ width: `${pct}%` }}
                            />
                        </div>
                    </div>

                    {inProgress && (
                        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                            <div className="bg-gray-50 rounded-lg px-3 py-2">
                                <div className="text-xs text-gray-500">Target to date</div>
                                <div className="font-medium text-gray-900 tabular-nums">
                                    {formatCurrency(proRataTarget)}
                                </div>
                            </div>
                            <div className="bg-gray-50 rounded-lg px-3 py-2">
                                <div className="text-xs text-gray-500">Pace</div>
                                <div className={`font-medium ${onTrack ? 'text-emerald-600' : 'text-amber-600'}`}>
                                    {onTrack === null ? '—' : onTrack ? 'On track' : 'Behind'}
                                </div>
                            </div>
                            <div className="bg-gray-50 rounded-lg px-3 py-2">
                                <div className="text-xs text-gray-500">Projected month end</div>
                                <div className="font-medium text-gray-900 tabular-nums">
                                    {projectedTotalIncGst === null || projectedTotalIncGst === undefined
                                        ? '—'
                                        : formatCurrency(projectedTotalIncGst)}
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

/** Set / change / clear one month's target. */
const TargetDialog = ({ open, initial, saving, error, onClose, onSave, onClear }) => {
    const [amount, setAmount] = useState('');
    const [applyRest, setApplyRest] = useState(false);

    useEffect(() => {
        if (!open) return;
        setAmount(initial?.target === null || initial?.target === undefined ? '' : String(initial.target));
        setApplyRest(false);
    }, [open, initial]);

    if (!open) return null;

    const parsed = amount.trim() === '' ? null : Number(amount);
    const invalid = parsed === null || Number.isNaN(parsed) || parsed < 0;

    return (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-md p-6">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-base font-semibold text-gray-900">
                        Sales target — {MONTH_NAMES[initial.month - 1]} {initial.year}
                    </h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <X size={18} />
                    </button>
                </div>

                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Target (inc GST)
                </label>
                <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                    <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        placeholder="0.00"
                        autoFocus
                        className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                </div>
                <p className="mt-1.5 text-xs text-gray-500">
                    Compared against invoiced income including GST, so it matches the totals above.
                </p>

                <label className="mt-4 flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={applyRest}
                        onChange={(e) => setApplyRest(e.target.checked)}
                        className="mt-0.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span>
                        Also apply to the rest of {initial.year}
                        <span className="block text-xs text-gray-500">
                            Only fills months with no target yet — anything you have already set is left alone.
                        </span>
                    </span>
                </label>

                {error && (
                    <div className="mt-3 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</div>
                )}

                <div className="mt-5 flex items-center justify-between gap-2">
                    {initial.target !== null && initial.target !== undefined ? (
                        <button
                            onClick={onClear}
                            disabled={saving}
                            className="text-sm text-red-600 hover:text-red-700 disabled:opacity-50"
                        >
                            Clear target
                        </button>
                    ) : <span />}
                    <div className="flex items-center gap-2">
                        <button
                            onClick={onClose}
                            className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={() => onSave({ targetAmount: parsed, applyToRemainingMonths: applyRest })}
                            disabled={invalid || saving}
                            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                        >
                            {saving ? <Loader size={14} className="animate-spin" /> : <Save size={14} />}
                            Save target
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

/**
 * The numeric half of the income view: the tax-ready totals and the month-by-month table.
 *
 * Split out of {@link IncomeTab} when the chart arrived, so each tab is one component
 * rather than two screens of JSX behind a pair of conditionals. Renders only what the API
 * returns and adds up nothing of its own.
 */
const IncomeNumbers = ({ annualIncome, currentMonthRef, onEditTarget }) => (
    <>
        {/* ---- Tax-ready totals ---- */}
        <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">
                {annualIncome.label} totals
                <span className="ml-2 font-normal text-gray-500">
                    {annualIncome.invoiceCount} invoice{annualIncome.invoiceCount === 1 ? '' : 's'}
                </span>
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
                <StatTile
                    label="Sales ex GST"
                    value={formatCurrency(annualIncome.subtotalExGst)}
                    sub="Goes on the tax return"
                    icon={FileText}
                />
                <StatTile
                    label="GST"
                    value={formatCurrency(annualIncome.gst)}
                    sub="Collected on those sales"
                    icon={Percent}
                />
                <StatTile
                    label="Total inc GST"
                    value={formatCurrency(annualIncome.totalIncGst)}
                    sub="Invoiced for the period"
                    icon={DollarSign}
                />
                <StatTile
                    label="Collected"
                    value={formatCurrency(annualIncome.collected)}
                    tone="positive"
                    sub="Received against those invoices"
                />
                <StatTile
                    label="Outstanding"
                    value={formatCurrency(annualIncome.outstanding)}
                    tone={Number(annualIncome.outstanding) > 0 ? 'negative' : 'muted'}
                    sub="Billed but not yet banked"
                    icon={Clock}
                />
                <StatTile
                    label="Draft"
                    value={formatCurrency(annualIncome.draftTotal)}
                    tone={Number(annualIncome.draftTotal) > 0 ? 'negative' : 'muted'}
                    sub="Work not billed at all yet"
                    icon={FileText}
                />
                <StatTile
                    label="Possible"
                    value={formatCurrency(annualIncome.possibleTotal)}
                    sub="If every draft were billed and paid"
                    icon={DollarSign}
                />
            </div>
            <p className="mt-2 text-xs text-gray-500">
                Counts each invoice in the month it was issued. <strong>Collected</strong> is
                money received against invoices issued in this period — not a cash-basis
                figure for the period itself. <strong>Draft</strong> is work in the shop that
                has not been billed; it cannot overlap the other figures, because an invoice
                has exactly one status. Quotes are excluded from all of them.
            </p>
        </div>

        {/* ---- Month by month ---- */}
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200 gap-3 flex-wrap">
                <h3 className="text-sm font-semibold text-gray-900">Month by month</h3>
                <span className="text-xs text-gray-500">
                    Target set for {annualIncome.monthsWithTarget} of {annualIncome.months.length} months
                    {annualIncome.monthsWithTarget > 0 && ` · ${formatCurrency(annualIncome.targetTotal)} combined`}
                </span>
            </div>

            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-white border-b border-gray-100">
                        <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                            <th className="px-4 py-2.5 font-medium">Month</th>
                            <th className="px-4 py-2.5 font-medium text-right">Invoices</th>
                            <th className="px-4 py-2.5 font-medium text-right">Ex GST</th>
                            <th className="px-4 py-2.5 font-medium text-right">GST</th>
                            <th className="px-4 py-2.5 font-medium text-right">Inc GST</th>
                            <th className="px-4 py-2.5 font-medium text-right">Collected</th>
                            <th className="px-4 py-2.5 font-medium text-right">Target</th>
                            <th className="px-4 py-2.5 font-medium text-right">vs Target</th>
                            <th className="px-4 py-2.5 font-medium text-right">%</th>
                            <th className="px-4 py-2.5 font-medium" />
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {annualIncome.months.map((m) => {
                            const isCurrent = m.year === currentMonthRef.year
                                && m.month === currentMonthRef.month;
                            return (
                                <tr key={`${m.year}-${m.month}`}
                                    className={isCurrent ? 'bg-indigo-50/40' : 'hover:bg-gray-50'}>
                                    <td className="px-4 py-2.5 font-medium text-gray-900 whitespace-nowrap">
                                        {m.label}
                                        {isCurrent && (
                                            <span className="ml-1.5 text-xs font-normal text-indigo-600">
                                                current
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-4 py-2.5 text-right tabular-nums text-gray-600">
                                        {m.invoiceCount}
                                    </td>
                                    <td className="px-4 py-2.5 text-right tabular-nums text-gray-600">
                                        {formatCurrency(m.subtotalExGst)}
                                    </td>
                                    <td className="px-4 py-2.5 text-right tabular-nums text-gray-600">
                                        {formatCurrency(m.gst)}
                                    </td>
                                    <td className="px-4 py-2.5 text-right tabular-nums font-medium text-gray-900">
                                        {formatCurrency(m.totalIncGst)}
                                    </td>
                                    <td className="px-4 py-2.5 text-right tabular-nums text-gray-600">
                                        {formatCurrency(m.collected)}
                                    </td>
                                    <td className="px-4 py-2.5 text-right tabular-nums text-gray-600">
                                        {m.target === null || m.target === undefined
                                            ? '—' : formatCurrency(m.target)}
                                    </td>
                                    <td className={`px-4 py-2.5 text-right tabular-nums ${
                                        m.targetVariance === null || m.targetVariance === undefined
                                            ? 'text-gray-400'
                                            : Number(m.targetVariance) >= 0
                                                ? 'text-emerald-600' : 'text-red-600'
                                    }`}>
                                        {formatSignedCurrency(m.targetVariance)}
                                    </td>
                                    <td className="px-4 py-2.5 text-right tabular-nums text-gray-600">
                                        {formatPercent(m.targetPercent)}
                                    </td>
                                    <td className="px-4 py-2.5 text-right">
                                        <button
                                            onClick={() => onEditTarget({
                                                year: m.year, month: m.month, target: m.target,
                                            })}
                                            title={m.target === null ? 'Set target' : 'Change target'}
                                            className="text-gray-400 hover:text-indigo-600"
                                        >
                                            <Target size={15} />
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                    <tfoot className="bg-gray-50 border-t border-gray-200">
                        <tr className="font-semibold text-gray-900">
                            <td className="px-4 py-2.5">{annualIncome.label}</td>
                            <td className="px-4 py-2.5 text-right tabular-nums">{annualIncome.invoiceCount}</td>
                            <td className="px-4 py-2.5 text-right tabular-nums">{formatCurrency(annualIncome.subtotalExGst)}</td>
                            <td className="px-4 py-2.5 text-right tabular-nums">{formatCurrency(annualIncome.gst)}</td>
                            <td className="px-4 py-2.5 text-right tabular-nums">{formatCurrency(annualIncome.totalIncGst)}</td>
                            <td className="px-4 py-2.5 text-right tabular-nums">{formatCurrency(annualIncome.collected)}</td>
                            <td className="px-4 py-2.5 text-right tabular-nums">{formatCurrency(annualIncome.targetTotal)}</td>
                            <td className="px-4 py-2.5" colSpan={3} />
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    </>
);

/**
 * Income tab: annual totals for tax, plus month-by-month performance against target.
 *
 * Exported alongside the page so the Chart/Numbers switch can be tested without standing
 * up the router and the rest of the invoices screen.
 *
 * Deliberately renders only what the API returns. The tax figures a workshop files on and
 * the target percentages it is judged by are computed and unit tested server-side, so this
 * component never adds up a column of its own.
 */
export const IncomeTab = () => {
    const dispatch = useDispatch();
    const {
        annualIncome = null,
        annualIncomeLoading = false,
        annualIncomeError = null,
        monthlyIncome = null,
        monthlyIncomeLoading = false,
        weeklyIncome = null,
        weeklyIncomeLoading = false,
        weeklyIncomeError = null,
        incomeYears = null,
        targetSaving = false,
        targetError = null,
    } = useSelector(state => state.invoices) || {};

    const today = useMemo(() => new Date(), []);
    // AU financial years run Jul-Jun and are labelled by the year they END in, so anything
    // from July belongs to the next FY. This is the one date rule the UI has to know, and it
    // only decides which year is preselected.
    const currentFinancialYear = today.getMonth() >= 6 ? today.getFullYear() + 1 : today.getFullYear();

    const [basis, setBasis] = useState('FINANCIAL');
    const [year, setYear] = useState(currentFinancialYear);
    const [targetDialog, setTargetDialog] = useState(null);
    // The chart leads: the shape of the year is the question an owner opens this tab with,
    // and the table is one click away for the figures they act on.
    const [chartView, setChartView] = useState('CHART');
    // Weekly opens first: the question an owner has on any given day is "where is THIS
    // month up to and what is left to collect", and that is the only view that answers
    // it. The year is one click away for the times they want the shape of it.
    const [chartMode, setChartMode] = useState(CHART_MODES.WEEKLY);

    const currentMonthRef = useMemo(() => ({
        year: today.getFullYear(),
        month: today.getMonth() + 1,
    }), [today]);

    const isWeekly = chartMode === CHART_MODES.WEEKLY;

    useEffect(() => { dispatch(fetchIncomeYears()); }, [dispatch]);
    useEffect(() => { dispatch(fetchMonthlyIncome(currentMonthRef)); }, [dispatch, currentMonthRef]);
    useEffect(() => { dispatch(fetchAnnualIncome({ year, basis })); }, [dispatch, year, basis]);
    // Guarded on the view rather than fetched unconditionally: it is a per-day aggregate
    // and someone who switches to Monthly and stays there should not keep paying for it.
    // Weekly being the default does mean it loads on mount, which is the point of making
    // it the default.
    useEffect(() => {
        if (isWeekly) dispatch(fetchWeeklyIncome(currentMonthRef));
    }, [dispatch, isWeekly, currentMonthRef]);

    // Switching basis re-points the year: FY2027 and calendar 2027 are different periods,
    // and carrying the number across would silently show a year the workshop didn't ask for.
    const handleBasisChange = (nextBasis) => {
        setBasis(nextBasis);
        setYear(nextBasis === 'FINANCIAL' ? currentFinancialYear : today.getFullYear());
    };

    const yearOptions = basis === 'FINANCIAL'
        ? (incomeYears?.financialYears ?? [currentFinancialYear])
        : (incomeYears?.calendarYears ?? [today.getFullYear()]);

    const refreshArgs = {
        annual: { year, basis },
        monthly: currentMonthRef,
    };

    const handleSaveTarget = ({ targetAmount, applyToRemainingMonths }) => {
        dispatch(saveSalesTarget({
            year: targetDialog.year,
            month: targetDialog.month,
            targetAmount,
            applyToRemainingMonths,
            refresh: refreshArgs,
        })).then((action) => {
            if (!action.error) setTargetDialog(null);
        });
    };

    const handleClearTarget = () => {
        dispatch(removeSalesTarget({
            year: targetDialog.year,
            month: targetDialog.month,
            refresh: refreshArgs,
        })).then((action) => {
            if (!action.error) setTargetDialog(null);
        });
    };

    return (
        <div className="space-y-6">
            {/* ---- This month vs target ---- */}
            <MonthProgressCard
                performance={monthlyIncome}
                loading={monthlyIncomeLoading}
                onEditTarget={() => setTargetDialog({
                    year: monthlyIncome?.income?.year ?? currentMonthRef.year,
                    month: monthlyIncome?.income?.month ?? currentMonthRef.month,
                    target: monthlyIncome?.income?.target ?? null,
                })}
            />

            {/* ---- Year selector ---- */}
            <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center rounded-lg border border-gray-200 overflow-hidden">
                    {[
                        { key: 'FINANCIAL', label: 'Financial year' },
                        { key: 'CALENDAR', label: 'Calendar year' },
                    ].map(({ key, label }) => (
                        <button
                            key={key}
                            onClick={() => handleBasisChange(key)}
                            className={`px-3 py-1.5 text-sm font-medium ${
                                basis === key
                                    ? 'bg-indigo-600 text-white'
                                    : 'bg-white text-gray-600 hover:bg-gray-50'
                            }`}
                        >
                            {label}
                        </button>
                    ))}
                </div>

                <select
                    value={year}
                    onChange={(e) => setYear(Number(e.target.value))}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                    {yearOptions.map((y) => (
                        <option key={y} value={y}>
                            {basis === 'FINANCIAL' ? `FY${y - 1}-${String(y % 100).padStart(2, '0')}` : y}
                        </option>
                    ))}
                </select>

                {annualIncome && (
                    <span className="text-xs text-gray-500">
                        {formatDate(annualIncome.periodStart)} – {formatDate(annualIncome.periodEnd)}
                    </span>
                )}
            </div>

            {annualIncomeError && (
                <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm flex items-center gap-2">
                    <AlertTriangle size={16} /> {annualIncomeError}
                </div>
            )}

            {annualIncomeLoading && !annualIncome && (
                <div className="bg-white rounded-2xl border border-gray-200 p-6 flex items-center gap-2 text-gray-500">
                    <Loader size={16} className="animate-spin" /> Loading {basis === 'FINANCIAL' ? 'financial' : 'calendar'} year…
                </div>
            )}

            {annualIncome && (
                <>
                    {/* ---- Chart / Numbers ---- */}
                    <div className="flex items-center gap-1 border-b border-gray-200">
                        {[
                            { key: 'CHART', label: 'Chart', icon: LineChart },
                            { key: 'NUMBERS', label: 'Numbers', icon: TableIcon },
                        ].map(({ key, label, icon: Icon }) => (
                            <button
                                key={key}
                                onClick={() => setChartView(key)}
                                aria-current={chartView === key ? 'page' : undefined}
                                className={`flex items-center gap-2 px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                                    chartView === key
                                        ? 'border-indigo-600 text-indigo-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                            >
                                <Icon size={15} /> {label}
                            </button>
                        ))}
                    </div>

                    {chartView === 'CHART' && (
                        <div className="space-y-3">
                            {/* Ordered narrowest window first, so the row reads as one
                                zoom control: this month by week, then the year by month,
                                then the year as a running total. Weekly answers "where is
                                this month up to"; monthly reads each month against its own
                                target; cumulative answers "are we behind for the year". */}
                            <div className="flex items-center rounded-lg border border-gray-200 overflow-hidden w-fit">
                                {[
                                    // ⚠️ THIS MONTH ONLY. A year of weeks is fifty-two
                                    // points on a card built for twelve, and the question
                                    // it answers is only ever about the month in front of you.
                                    { key: CHART_MODES.WEEKLY, label: 'Weekly' },
                                    { key: CHART_MODES.MONTHLY, label: 'Monthly' },
                                    { key: CHART_MODES.CUMULATIVE, label: 'Cumulative' },
                                ].map(({ key, label }) => (
                                    <button
                                        key={key}
                                        onClick={() => setChartMode(key)}
                                        aria-pressed={chartMode === key}
                                        className={`px-3 py-1.5 text-sm font-medium ${
                                            chartMode === key
                                                ? 'bg-indigo-600 text-white'
                                                : 'bg-white text-gray-600 hover:bg-gray-50'
                                        }`}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>

                            <IncomeChart
                                months={isWeekly ? (weeklyIncome?.weeks ?? []) : annualIncome.months}
                                mode={chartMode}
                                currentYear={currentMonthRef.year}
                                currentMonth={currentMonthRef.month}
                                periodLabel={isWeekly
                                    ? (weeklyIncome?.label ?? 'this month')
                                    : annualIncome.label}
                                onSetTarget={() => setTargetDialog({
                                    year: currentMonthRef.year,
                                    month: currentMonthRef.month,
                                    target: null,
                                })}
                            />
                        </div>
                    )}

                    {chartView === 'NUMBERS' && (
                        <IncomeNumbers
                            annualIncome={annualIncome}
                            currentMonthRef={currentMonthRef}
                            onEditTarget={setTargetDialog}
                        />
                    )}
                </>
            )}

            <TargetDialog
                open={!!targetDialog}
                initial={targetDialog || { year: currentMonthRef.year, month: currentMonthRef.month, target: null }}
                saving={targetSaving}
                error={targetError}
                onClose={() => setTargetDialog(null)}
                onSave={handleSaveTarget}
                onClear={handleClearTarget}
            />
        </div>
    );
};

const InvoicesPage = () => {
    const dispatch = useDispatch();

    const invoiceState = useSelector(state => state.invoices) || {};
    const {
        invoices = [],
        invoicesLoading = false,
        invoicesError = null,
        pagination = { page: 0, size: 8, totalElements: 0, totalPages: 0 },
        statusFilter = 'all',
        serverFilter = { customerQuery: '', vehicleRego: '', dateFrom: '', dateTo: '', active: false },
        selectedInvoice = null,
        showDetailPanel = false,
        detailLoading = false,
        operationLoading = false,
        operationSuccess = null,
        operationError = null,
        bulkReconcileResult = null,
        pdfLoadingUuid = null,
        archivePagination = { page: 0, size: 20, totalElements: 0, totalPages: 0 },
        lineItemTypes = [],
        paymentMethods = [],
    } = invoiceState;

    // Backend-driven line item options for the "Add Line Item / Adjustment"
    // dropdown — falls back to the local default if the GET hasn't loaded yet.
    const lineTypeOptions = lineItemTypes.length > 0
        ? lineItemTypes
        : LINE_TYPE_OPTIONS_FALLBACK;

    // Backend-driven payment method options for the Record Payment dialog —
    // falls back to a minimal default if the GET hasn't loaded yet.
    const paymentMethodOptions = paymentMethods.length > 0
        ? paymentMethods
        : PAYMENT_METHOD_FALLBACK;

    // ---- Top-level view tab: 'invoices' or 'archive' ----
    const [activeTab, setActiveTab] = useState('invoices');

    /*
     * ── Accounting integration gate ────────────────────────────────────────
     *
     * Reconciling means "this invoice is settled in the accounting system". With no
     * accounting system connected the action is meaningless, so it is not offered at all
     * rather than shown and then explained away.
     *
     * Null while unknown — DISTINCT from an empty list. Rendering the buttons before the
     * answer arrives and hiding them a moment later reads as a bug, so nothing shows until
     * we know. A failed lookup stays null and simply hides the feature: wrongly offering
     * reconcile is worse than briefly not offering it.
     */
    const [connectedSoftware, setConnectedSoftware] = useState(null);

    useEffect(() => {
        let cancelled = false;
        listAccountingIntegrationsApi()
            .then((rows) => {
                if (cancelled) return;
                setConnectedSoftware((rows ?? []).filter(r => r.enabled).map(r => r.software));
            })
            .catch(() => { /* leave null — feature stays hidden */ });
        return () => { cancelled = true; };
    }, []);

    const hasAccountingIntegration = (connectedSoftware?.length ?? 0) > 0;

    /*
     * Batch reconcile lives on the PAID tab only.
     *
     * The invoice's own work finishes at fully paid. Reconciling is a separate, later step —
     * and for a workshop that adopts accounting software after the fact, it is a backlog of
     * already-paid invoices to work through, which is exactly what the Paid tab is. Showing
     * checkboxes on Draft or Void would offer an action that can never apply there.
     *
     * A single invoice can still be reconciled from its detail panel the moment it is paid;
     * that is the same gate plus the invoice's own status.
     */
    const canBatchReconcile = hasAccountingIntegration && statusFilter === 'PAID';

    /*
     * Invoices ticked for bulk reconcile. Only PAID invoices are selectable — the server
     * rejects anything else, and offering a checkbox that always fails is a trap.
     */
    const [selectedForReconcile, setSelectedForReconcile] = useState([]);
    // Which job link was pressed, so it can spin while the board loads behind it.
    const [openingJobUuid, setOpeningJobUuid] = useState(null);

    const toggleReconcileSelection = (uuid) => {
        setSelectedForReconcile(prev => {
            if (prev.includes(uuid)) return prev.filter(u => u !== uuid);
            // Silently ignoring the tick would look broken; the checkbox is already
            // disabled at the cap, so this only backstops a stale render.
            if (prev.length >= MAX_RECONCILE_BATCH) return prev;
            return [...prev, uuid];
        });
    };

    const handleBulkReconcile = async () => {
        if (selectedForReconcile.length === 0) return;
        const result = await dispatch(reconcileInvoicesBatch(selectedForReconcile));
        // Untick only what actually reconciled. The invoice that halted the run and
        // everything after it were left untouched, so they stay selected and "run the rest
        // as a new batch" is one action rather than rebuilding the selection by hand.
        const done = new Set((result.payload?.results ?? [])
            .filter(r => r.success).map(r => r.invoiceUuid));
        setSelectedForReconcile(prev => prev.filter(u => !done.has(u)));
        loadInvoices();
    };

    // ---- Local copy of the advanced filter form (dates default to current month) ----
    const invoiceDefaultRange = useMemo(() => getCurrentMonthRange(), []);
    const [filterForm, setFilterForm] = useState({
        customerQuery: serverFilter.customerQuery || '',
        vehicleRego: serverFilter.vehicleRego || '',
        dateFrom: serverFilter.dateFrom || invoiceDefaultRange.dateFrom,
        dateTo: serverFilter.dateTo || invoiceDefaultRange.dateTo,
        // Which date the window filters. ISSUE is what it silently was before there
        // was a choice, so an existing habit keeps giving the same answers.
        dateField: serverFilter.dateField || 'ISSUE',
    });

    // loadInvoices — dispatches based on current statusFilter and page.
    // The 'PARTIALLY_PAID' tab uses /invoices/outstanding so it includes
    // both PARTIALLY_PAID and TRADE_CREDIT invoices in one paginated view.
    // Kept as a stable callback for the refresh button and clear-filter flow.
    const loadInvoices = useCallback(() => {
        const params = { page: pagination.page, size: pagination.size };
        if (statusFilter === 'PARTIALLY_PAID') {
            dispatch(fetchOutstandingInvoices(params));
        } else if (statusFilter && statusFilter !== 'all') {
            dispatch(fetchInvoicesByStatus({ status: statusFilter, ...params }));
        } else {
            dispatch(fetchInvoices(params));
        }
    }, [dispatch, statusFilter, pagination.page, pagination.size]);

    // Initial load on mount only — tab changes are handled directly in handleTabChange.
    useEffect(() => { loadInvoices(); }, []);

    /**
     * 🔑 `?invoice=<uuid>` opens that invoice.
     *
     * What the job card links to: following an invoice from a job has to ARRIVE at the
     * invoice, not at a list the person then has to search.
     */
    const openInvoiceFromLink = useCallback((uuid) => { dispatch(fetchInvoice(uuid)); }, [dispatch]);
    useConsumeRecordParam(INVOICE_PARAM, openInvoiceFromLink);

    // Load the line item type dropdown options once on mount.
    useEffect(() => { dispatch(fetchLineItemTypes()); }, [dispatch]);
    useEffect(() => { dispatch(fetchPaymentMethods()); }, [dispatch]);

    // Page changes trigger a fresh load (pagination buttons).
    useEffect(() => {
        if (!serverFilter.active) {
            const params = { page: pagination.page, size: pagination.size };
            if (statusFilter === 'PARTIALLY_PAID') {
                dispatch(fetchOutstandingInvoices(params));
            } else if (statusFilter && statusFilter !== 'all') {
                dispatch(fetchInvoicesByStatus({ status: statusFilter, ...params }));
            } else {
                dispatch(fetchInvoices(params));
            }
        }
    }, [pagination.page]);

    // Auto-dismiss toasts
    useEffect(() => {
        if (operationSuccess || operationError) {
            const timer = setTimeout(() => dispatch(clearOperationStatus()), 3500);
            return () => clearTimeout(timer);
        }
    }, [operationSuccess, operationError, dispatch]);

    // ---- Advanced server-side search (backend) ----
    const handleAdvancedSearch = useCallback(() => {
        const hasQuery = filterForm.customerQuery || filterForm.vehicleRego || filterForm.dateFrom || filterForm.dateTo;
        if (!hasQuery) { loadInvoices(); return; }
        // 🔑 The status tab goes WITH the search. Without it the search ran across every
        // status and the result was shown under a heading reading "no sent invoices
        // found" — which says "this customer has none", not "your filter was ignored".
        const withStatus = { ...filterForm, status: statusFilter };
        dispatch(setServerFilter(withStatus));
        dispatch(advancedSearchInvoices({ ...withStatus, page: 0, size: pagination.size }));
    }, [dispatch, filterForm, statusFilter, pagination.size, loadInvoices]);

    const handleClearAdvancedSearch = useCallback(() => {
        const range = getCurrentMonthRange();
        setFilterForm({ customerQuery: '', vehicleRego: '', dateFrom: range.dateFrom,
            dateTo: range.dateTo, dateField: 'ISSUE' });
        dispatch(clearServerFilter());
        loadInvoices();
    }, [dispatch, loadInvoices]);

    const handleInvoiceSearchKeyDown = (e) => {
        if (e.key === 'Enter') handleAdvancedSearch();
        if (e.key === 'Escape') handleClearAdvancedSearch();
    };

    // When pagination changes while advanced search is active, re-run the search
    useEffect(() => {
        if (serverFilter.active) {
            dispatch(advancedSearchInvoices({ ...serverFilter, page: pagination.page, size: pagination.size }));
        }
    }, [pagination.page]);

    // ---- Client-side instant search (filters what's already loaded on this page) ----
    const filteredInvoices = invoices;

    // Group the invoices by status for the "All" tab view
    const groupedInvoices = useMemo(() => {
        const groups = { DRAFT: [], SENT: [], PARTIALLY_PAID: [], TRADE_CREDIT: [], OVERDUE: [], PAID: [], VOID: [], RECONCILED: [] };
        invoices.forEach(inv => { if (groups[inv.status]) groups[inv.status].push(inv); });
        return groups;
    }, [invoices]);

    const stats = useMemo(() => {
        const totalPaid = invoices.filter(i => i.status === 'PAID').reduce((s, i) => s + (i.totalAmount || 0), 0);
        const totalOutstanding = invoices.filter(i => ['SENT', 'PARTIALLY_PAID', 'TRADE_CREDIT', 'OVERDUE'].includes(i.status)).reduce((s, i) => s + (i.balanceDue || 0), 0);
        const totalOverdue = invoices.filter(i => i.status === 'OVERDUE').reduce((s, i) => s + (i.balanceDue || 0), 0);
        const draftCount = invoices.filter(i => i.status === 'DRAFT').length;
        return { totalPaid, totalOutstanding, totalOverdue, draftCount };
    }, [invoices]);

    const handleRowClick = useCallback((invoice) => {
        dispatch(fetchInvoice(invoice.uuid));
    }, [dispatch]);

    /**
     * The job behind an invoice — on the JOBS BOARD, not in a card over this page.
     *
     * 🔑 It used to open here, which left the invoice table AND the invoice side panel
     * standing behind the job card: three things on screen, two of them about the
     * record you had just left. Following the link now takes you to the board with the
     * job open over it and no invoice UI behind, which is the mirror of what a job's
     * invoice link does.
     */
    const goToJob = useOpenJob();
    const openJob = useCallback((uuid) => {
        // The control disables itself while the board loads, so a second press cannot
        // fire a second navigation.
        if (openingJobUuid) return;
        setOpeningJobUuid(uuid);
        goToJob(uuid);
    }, [goToJob, openingJobUuid]);

    const hasActiveFilter = serverFilter.active || serverFilter.customerQuery || serverFilter.vehicleRego || serverFilter.dateFrom || serverFilter.dateTo;

    // ---- Invoice row actions ----
    // The menu renders through a portal (OverflowSafeMenu) rather than as an absolutely
    // positioned child. It used to be `absolute ... z-50` inside the table's
    // `overflow-x-auto` wrapper, which clipped it mid-item — z-index cannot rescue an
    // element from an ancestor's overflow. See OverflowSafeMenu for the full reasoning.
    const InvoiceActions = ({ invoice }) => (
        <div className="flex items-center justify-end gap-1">
            <button onClick={(e) => { e.stopPropagation(); handleRowClick(invoice); }}
                    className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" title="Open Details">
                <Eye size={16} />
            </button>
            {/* PDF open-in-tab button — with per-invoice spinner */}
            <PdfButton invoice={invoice} pdfLoadingUuid={pdfLoadingUuid} dispatch={dispatch} variant="icon" />
            <OverflowSafeMenu trigger={<MoreVertical size={16} />} triggerTitle="Invoice actions">
                {({ close }) => (
                    <>
                        <MenuItem icon={<Eye size={14} />}
                                  onClick={(e) => { e.stopPropagation(); handleRowClick(invoice); close(); }}>
                            Open Details
                        </MenuItem>
                        <MenuItem icon={<ExternalLink size={14} />}
                                  onClick={(e) => {
                                      e.stopPropagation();
                                      dispatch(openInvoicePdf({ uuid: invoice.uuid, invoiceNumber: invoice.invoiceNumber }));
                                      close();
                                  }}>
                            View PDF
                        </MenuItem>
                        <MenuItem icon={<Download size={14} />}
                                  onClick={(e) => {
                                      e.stopPropagation();
                                      dispatch(downloadInvoicePdf({ uuid: invoice.uuid, invoiceNumber: invoice.invoiceNumber }));
                                      close();
                                  }}>
                            Download PDF
                        </MenuItem>
                        {invoice.status !== 'PAID' && invoice.status !== 'VOID' && (
                            <MenuItem danger icon={<Ban size={14} />}
                                      onClick={(e) => { e.stopPropagation(); handleRowClick(invoice); close(); }}>
                                Void Invoice…
                            </MenuItem>
                        )}
                    </>
                )}
            </OverflowSafeMenu>
        </div>
    );

    // ---- Invoice table ----
    const InvoiceTable = ({ invoices: tableInvoices, emptyMessage }) => (
        <>
            {/* Batch action bar — only while something is ticked, so it never takes space
                on a normal day. Reconciling is irreversible, hence the explicit count. */}
            {canBatchReconcile && selectedForReconcile.length > 0 && (
                <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-teal-50 border-b border-teal-100">
                    <span className="text-sm text-teal-800">
                        {selectedForReconcile.length} invoice{selectedForReconcile.length === 1 ? '' : 's'} selected
                        {/* The limit is only worth mentioning once it actually bites. */}
                        {selectedForReconcile.length >= MAX_RECONCILE_BATCH && (
                            <span className="ml-2 text-teal-600">
                                — {MAX_RECONCILE_BATCH} is the most in one go
                            </span>
                        )}
                    </span>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => setSelectedForReconcile([])}
                            className="px-3 py-1.5 text-sm text-teal-700 hover:bg-teal-100 rounded-lg"
                        >
                            Clear
                        </button>
                        <button
                            type="button"
                            onClick={handleBulkReconcile}
                            disabled={operationLoading}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 disabled:opacity-50"
                        >
                            {operationLoading
                                ? <RefreshCw size={14} className="animate-spin" />
                                : <Archive size={14} />}
                            Reconcile selected
                        </button>
                    </div>
                </div>
            )}

            {/* Per-invoice outcomes. Shown ONLY when something failed: a batch that fully
                succeeded already says so in the toast, and repeating it here would train
                people to dismiss this panel without reading it. */}
            {/* Shown ONLY when a run halted. A clean batch already says so in the toast,
                and repeating it here trains people to dismiss this panel unread. */}
            {canBatchReconcile && bulkReconcileResult?.failed > 0 && (
                <div className="px-4 py-2.5 bg-amber-50 border-b border-amber-100">
                    <p className="text-sm font-medium text-amber-900">
                        Stopped after {bulkReconcileResult.succeeded} reconciled
                    </p>
                    {bulkReconcileResult.results.filter(r => !r.success).map(r => (
                        <p key={r.invoiceUuid} className="mt-1 text-xs text-amber-800">
                            <span className="font-mono">
                                {r.invoiceNumber || r.invoiceUuid.substring(0, 8)}
                            </span>
                            {' — '}{r.error}
                        </p>
                    ))}
                    {bulkReconcileResult.notAttempted > 0 && (
                        <p className="mt-1 text-xs text-amber-800">
                            {bulkReconcileResult.notAttempted} invoice
                            {bulkReconcileResult.notAttempted === 1 ? ' was' : 's were'} not
                            attempted and {bulkReconcileResult.notAttempted === 1 ? 'is' : 'are'} unchanged.
                        </p>
                    )}
                    <p className="mt-1.5 text-xs text-amber-700">
                        The reconciled ones are done. Deselect the invoice above and run the
                        rest as a new batch.
                    </p>
                </div>
            )}

            {tableInvoices.length === 0 ? (
                <div className="text-center py-12 text-gray-500 text-sm">{emptyMessage || 'No invoices found'}</div>
            ) : (
                <div className="overflow-x-auto">
                    {/* This wrapper used to read `overflow-x-auto overflow-y-visible`, which
                        did nothing: the CSS spec computes a `visible` axis to `auto` when the
                        other axis is not visible, so it clipped the row menu it was written to
                        let through. The menu now escapes via a portal — see OverflowSafeMenu. */}
                    <table className="w-full">
                        <thead>
                        <tr className="border-b border-gray-100">
                            {/* Selection column only exists when reconciling is possible. */}
                            {canBatchReconcile && (
                                <th className="w-10 p-4">
                                    <span className="sr-only">Select for reconcile</span>
                                </th>
                            )}
                            <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice</th>
                            <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Booking</th>
                            <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Job</th>
                            <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                            <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Vehicle</th>
                            <th className="text-right p-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Estimated</th>
                            <th className="text-right p-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                            <th className="text-center p-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                            <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                            <th className="text-right p-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        {tableInvoices.map((invoice) => (
                            <tr key={invoice.uuid} onClick={() => handleRowClick(invoice)}
                                className="border-b border-gray-50 hover:bg-indigo-50/30 transition-colors cursor-pointer">
                                {canBatchReconcile && (
                                    <td className="p-4" onClick={(e) => e.stopPropagation()}>
                                        {/* Only PAID invoices can be reconciled. Rendering a
                                            disabled box on the others explains why the row
                                            cannot be picked, rather than leaving a blank.
                                            Boxes also stop accepting ticks at the batch cap —
                                            already-ticked ones stay live so you can swap. */}
                                        <input
                                            type="checkbox"
                                            checked={selectedForReconcile.includes(invoice.uuid)}
                                            disabled={invoice.status !== 'PAID'
                                                || (selectedForReconcile.length >= MAX_RECONCILE_BATCH
                                                    && !selectedForReconcile.includes(invoice.uuid))}
                                            onChange={() => toggleReconcileSelection(invoice.uuid)}
                                            title={invoice.status !== 'PAID'
                                                ? 'Only paid invoices can be reconciled'
                                                : selectedForReconcile.length >= MAX_RECONCILE_BATCH
                                                    && !selectedForReconcile.includes(invoice.uuid)
                                                    ? `Up to ${MAX_RECONCILE_BATCH} invoices can be reconciled at once`
                                                    : 'Select for reconcile'}
                                            className="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500 disabled:opacity-30"
                                        />
                                    </td>
                                )}
                                <td className="p-4">
                                    <span className="font-mono text-sm font-medium text-indigo-600">{invoice.invoiceNumber}</span>
                                </td>
                                <td className="p-4">
                                    <span className="font-mono text-xs text-gray-500">{invoice.bookingCode || '—'}</span>
                                </td>
                                <td className="p-4">
                                    {invoice.jobNumber ? (
                                        <RecordCrossLink
                                            variant="inline"
                                            uuid={invoice.jobUuid}
                                            label={invoice.jobNumber}
                                            onOpen={openJob}
                                            loading={openingJobUuid === invoice.jobUuid}
                                            title={`Open job ${invoice.jobNumber}`}
                                        />
                                    ) : (
                                        <span className="font-mono text-xs text-gray-500">—</span>
                                    )}
                                </td>
                                <td className="p-4">
                                    <p className="font-medium text-gray-900">{invoice.customerName}</p>
                                    {invoice.customerPhone && <p className="text-xs text-gray-500">{invoice.customerPhone}</p>}
                                </td>
                                <td className="p-4 text-sm text-gray-600">{invoice.vehicleRego || '—'}</td>
                                <td className="p-4 text-right">
                                    {invoice.estimatedValue
                                        ? <span className="text-sm text-gray-500">{formatCurrency(invoice.estimatedValue)}</span>
                                        : <span className="text-gray-400">—</span>}
                                </td>
                                <td className="p-4 text-right">
                                    <span className="font-semibold text-gray-900">{formatCurrency(invoice.totalAmount)}</span>
                                    {invoice.balanceDue > 0 && invoice.balanceDue !== invoice.totalAmount && (
                                        <p className="text-xs text-amber-600">Due: {formatCurrency(invoice.balanceDue)}</p>
                                    )}
                                </td>
                                <td className="p-4 text-center"><StatusBadge status={invoice.status} /></td>
                                <td className="p-4 text-sm text-gray-600">{formatDate(invoice.dueDate)}</td>
                                <td className="p-4" onClick={(e) => e.stopPropagation()}>
                                    <InvoiceActions invoice={invoice} />
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );

    // ---- Section headers for grouped "All" view ----
    const GROUP_LABELS = {
        DRAFT:          '📝 Draft Invoices',
        SENT:           '📤 Sent — Awaiting Payment',
        PARTIALLY_PAID: '💳 Partially Paid',
        TRADE_CREDIT:   '🤝 Trade Credit',
        OVERDUE:        '⚠️ Overdue',
        PAID:           '✅ Paid',
        VOID:           '🚫 Voided',
    };

    return (
        <Layout>
            <div className="p-6">
                {/* Toasts */}
                {operationSuccess && (
                    <div className="fixed top-4 right-4 bg-emerald-500 text-white px-4 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2 max-w-sm">
                        <CheckCircle size={18} /> {operationSuccess}
                    </div>
                )}
                {operationError && !showDetailPanel && (
                    <div className="fixed top-4 right-4 bg-red-500 text-white px-4 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2 max-w-sm">
                        <AlertTriangle size={18} /> {operationError}
                    </div>
                )}

                {/* Top-level view tabs */}
                <div className="flex items-center gap-1 mb-6 border-b border-gray-200">
                    <button
                        onClick={() => setActiveTab('invoices')}
                        className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
                            activeTab === 'invoices'
                                ? 'border-indigo-600 text-indigo-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    >
                        <FileText size={16} /> Invoices
                    </button>
                    <button
                        onClick={() => setActiveTab('archive')}
                        className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
                            activeTab === 'archive'
                                ? 'border-indigo-600 text-indigo-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    >
                        <Archive size={16} /> Reconciled
                    </button>
                    <button
                        onClick={() => setActiveTab('income')}
                        className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
                            activeTab === 'income'
                                ? 'border-indigo-600 text-indigo-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    >
                        <TrendingUp size={16} /> Income &amp; Targets
                    </button>
                </div>

                {/* Archive tab content */}
                {activeTab === 'archive' && <ArchiveTab />}

                {/* Income & targets tab content */}
                {activeTab === 'income' && <IncomeTab />}

                {/* Invoices tab content */}
                {activeTab === 'invoices' && (
                    <>
                        {/* Filters + Table card */}
                        <div className="bg-white rounded-2xl border border-gray-200 flex flex-col" style={{ minHeight: 'calc(100vh - 220px)' }}>

                            {/* ---- Toolbar ---- */}
                            <div className="p-4 border-b border-gray-100 space-y-3">
                                {/* Status tabs */}
                                <div className="flex items-center gap-1 flex-wrap">
                                    {STATUS_TABS.map(({ key, label }) => (
                                        <button key={key} onClick={() => {
                                            if (key === statusFilter) return;
                                            const range = getCurrentMonthRange();
                                            setFilterForm({ customerQuery: '', vehicleRego: '', dateFrom: range.dateFrom, dateTo: range.dateTo });
                                            dispatch(clearServerFilter());
                                            dispatch(setStatusFilter(key));
                                            const params = { page: 0, size: pagination.size };
                                            if (key === 'PARTIALLY_PAID') {
                                                dispatch(fetchOutstandingInvoices(params));
                                            } else if (key && key !== 'all') {
                                                dispatch(fetchInvoicesByStatus({ status: key, ...params }));
                                            } else {
                                                dispatch(fetchInvoices(params));
                                            }
                                        }}
                                                className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${statusFilter === key ? 'bg-indigo-100 text-indigo-700' : 'text-gray-600 hover:bg-gray-100'}`}>
                                            {label}
                                        </button>
                                    ))}
                                </div>

                                {/* Search row: dates + customer + rego + search button + clear + refresh */}
                                <div className="flex flex-wrap items-end gap-3">
                                    <div>
                                        <label className="block text-xs font-medium text-gray-500 mb-1 flex items-center gap-1">
                                            <Calendar size={11} /> From
                                        </label>
                                        <input
                                            type="date"
                                            value={filterForm.dateFrom}
                                            onChange={(e) => setFilterForm(f => ({ ...f, dateFrom: e.target.value }))}
                                            onKeyDown={handleInvoiceSearchKeyDown}
                                            className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-500 mb-1 flex items-center gap-1">
                                            <Calendar size={11} /> To
                                        </label>
                                        <input
                                            type="date"
                                            value={filterForm.dateTo}
                                            onChange={(e) => setFilterForm(f => ({ ...f, dateTo: e.target.value }))}
                                            onKeyDown={handleInvoiceSearchKeyDown}
                                            className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-500 mb-1 flex items-center gap-1">
                                            <Calendar size={11} /> Dates are
                                        </label>
                                        {/* 🔑 One date at a time. An invoice raised in
                                            March and due in April is inside an April
                                            window on one reading and outside it on the
                                            other — a window that quietly picks one is a
                                            window nobody can trust. */}
                                        <select
                                            aria-label="Which date the range filters"
                                            value={filterForm.dateField}
                                            onChange={(e) => setFilterForm(f => ({ ...f, dateField: e.target.value }))}
                                            className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none"
                                        >
                                            <option value="ISSUE">Invoice date</option>
                                            <option value="DUE">Due date</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-500 mb-1 flex items-center gap-1">
                                            <User size={11} /> Customer / Phone
                                        </label>
                                        <input
                                            type="text"
                                            value={filterForm.customerQuery}
                                            onChange={(e) => setFilterForm(f => ({ ...f, customerQuery: e.target.value }))}
                                            onKeyDown={handleInvoiceSearchKeyDown}
                                            placeholder="e.g. John or 0412…"
                                            className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none w-40"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-500 mb-1 flex items-center gap-1">
                                            <Car size={11} /> Rego
                                        </label>
                                        <input
                                            type="text"
                                            value={filterForm.vehicleRego}
                                            onChange={(e) => setFilterForm(f => ({ ...f, vehicleRego: e.target.value.toUpperCase() }))}
                                            onKeyDown={handleInvoiceSearchKeyDown}
                                            placeholder="e.g. ABC123"
                                            className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none w-28 uppercase"
                                        />
                                    </div>
                                    <button onClick={handleAdvancedSearch}
                                            className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors">
                                        <Search size={14} /> Search
                                    </button>
                                    {hasActiveFilter && (
                                        <button onClick={handleClearAdvancedSearch}
                                                className="px-3 py-2 text-gray-600 hover:bg-gray-200 rounded-lg text-sm transition-colors">
                                            Clear
                                        </button>
                                    )}
                                    <button onClick={loadInvoices} disabled={invoicesLoading}
                                            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" title="Refresh">
                                        <RefreshCw size={16} className={invoicesLoading ? 'animate-spin' : ''} />
                                    </button>

                                    {/* Inline stats */}
                                    <div className="flex items-center gap-3 ml-auto">
                                        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 rounded-lg">
                                            <Clock size={14} className="text-blue-600" />
                                            <span className="text-xs text-blue-600 font-medium">Outstanding</span>
                                            <span className="text-sm font-bold text-blue-700">{formatCurrency(stats.totalOutstanding)}</span>
                                        </div>
                                        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 rounded-lg">
                                            <AlertTriangle size={14} className="text-red-600" />
                                            <span className="text-xs text-red-600 font-medium">Overdue</span>
                                            <span className="text-sm font-bold text-red-700">{formatCurrency(stats.totalOverdue)}</span>
                                        </div>
                                        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 rounded-lg">
                                            <FileText size={14} className="text-gray-500" />
                                            <span className="text-xs text-gray-500 font-medium">Drafts</span>
                                            <span className="text-sm font-bold text-gray-700">{stats.draftCount}</span>
                                        </div>
                                    </div>
                                </div>

                                {hasActiveFilter && (
                                    <p className="text-xs text-indigo-600">
                                        Showing search results. Clear to return to normal view.
                                    </p>
                                )}
                            </div>

                            {/* ---- Loading / Error ---- */}
                            {invoicesLoading && (
                                <div className="flex flex-col items-center justify-center py-16 flex-1 gap-3">
                                    <div className="w-10 h-10 rounded-full border-4 border-indigo-100 border-t-indigo-600 animate-spin" />
                                    <p className="text-sm text-gray-500 font-medium">
                                        Loading {statusFilter === 'all' ? 'all invoices' : statusFilter.charAt(0) + statusFilter.slice(1).toLowerCase().replace('_', ' ') + ' invoices'}…
                                    </p>
                                </div>
                            )}
                            {invoicesError && !invoicesLoading && (
                                <div className="flex items-center justify-center py-12 text-red-500 flex-1">
                                    <AlertTriangle size={24} /><span className="ml-2">{invoicesError}</span>
                                </div>
                            )}

                            {/* ---- Table content ---- */}
                            {!invoicesLoading && !invoicesError && (
                                <div className="p-4 flex-1 overflow-auto">
                                    {statusFilter === 'all' ? (
                                        <>
                                            {Object.entries(GROUP_LABELS).map(([status, label]) =>
                                                groupedInvoices[status]?.length > 0 ? (
                                                    <div key={status} className="mb-6">
                                                        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                                            {label}
                                                            <span className="px-2 py-0.5 bg-gray-100 rounded-full text-xs">{groupedInvoices[status].length}</span>
                                                        </h3>
                                                        <InvoiceTable invoices={groupedInvoices[status]} />
                                                    </div>
                                                ) : null
                                            )}
                                            {filteredInvoices.length === 0 && (
                                                <div className="text-center py-12 text-gray-500">No invoices found</div>
                                            )}
                                        </>
                                    ) : (
                                        <InvoiceTable
                                            invoices={filteredInvoices}
                                            emptyMessage={`No ${statusFilter.toLowerCase().replace('_', ' ')} invoices found`}
                                        />
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Pagination footer */}
                        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-30">
                            <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
                                <p className="text-sm text-gray-500">
                                    {hasActiveFilter
                                        ? `${pagination.totalElements} results found`
                                        : `${pagination.totalElements} total invoices`
                                    }
                                </p>
                                <div className="flex items-center gap-2">
                                    <button onClick={() => dispatch(setPage(pagination.page - 1))} disabled={pagination.page === 0}
                                            className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed">
                                        <ChevronLeft size={16} /> Previous
                                    </button>
                                    <span className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-lg">{pagination.page + 1}</span>
                                    <span className="text-sm text-gray-500">of {pagination.totalPages || 1}</span>
                                    <button onClick={() => dispatch(setPage(pagination.page + 1))} disabled={pagination.page >= pagination.totalPages - 1}
                                            className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed">
                                        Next <ChevronRight size={16} />
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className="h-16" />
                    </>
                )}

                {/* Detail slide-over panel */}
                {showDetailPanel && (
                    <>
                        <div className="fixed inset-0 bg-black/30 z-40 transition-opacity" onClick={() => dispatch(closeDetailPanel())} />
                        <div className="fixed right-0 top-0 bottom-0 w-full max-w-2xl bg-white shadow-2xl z-50 flex flex-col transition-transform">
                            {selectedInvoice ? (
                                <InvoiceDetailPanel
                                    invoice={selectedInvoice}
                                    detailLoading={detailLoading}
                                    operationLoading={operationLoading}
                                    operationError={operationError}
                                    pdfLoadingUuid={pdfLoadingUuid}
                                    lineTypeOptions={lineTypeOptions}
                                    paymentMethodOptions={paymentMethodOptions}
                                    hasAccountingIntegration={hasAccountingIntegration}
                                    onOpenJob={openJob}
                                    openingJobUuid={openingJobUuid}
                                    dispatch={dispatch}
                                />
                            ) : (
                                /* Opened from a link, before the invoice itself has
                                   arrived: the same card shapes the panel is about to
                                   hold, rather than a bare spinner. */
                                <LaneLoading className="p-6" cards={2} label="Loading invoice details" />
                            )}
                        </div>
                    </>
                )}
            </div>
        </Layout>
    );
};

export default InvoicesPage;